/**
 * @deprecated Feb 2026 · iter-77 (part 9)
 *
 * The Company Manager (CEO) dashboard has been consolidated into the
 * universal `StaffDashboard.js`. The `/company-manager-dashboard` and
 * `/company-dashboard` routes now redirect to `/staff-dashboard`, and
 * `LoginPage.js` routes every admin / company_manager / manager /
 * supervisor / crew_leader / agent to that same universal dashboard.
 *
 * This file is retained for the "Regional Command" tab layout reference
 * only. DO NOT wire new features into this component — build them inside
 * `StaffDashboard.js` (or one of its sub-components under
 * `/app/frontend/src/components/staff/`).
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getInitials } from '../utils/initials';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DashboardShell from '../components/DashboardShell';
import TwoFABanner from '../components/TwoFABanner';
import { useBoardFilter, AdminBoardFilterContext } from '../components/UnifiedGeoRoleFilter';
import UnifiedGeoRoleFilter from '../components/UnifiedGeoRoleFilter';
import { adminScopeTerms } from '../lib/adminScopeTerms';
import { DepartmentNavigationProvider } from '../components/admin/DepartmentNavigation';
import { OperationsMenuTabs } from '../components/admin/OperationsMenuTabs';
import CommercialCleanersBoard from '../components/staff-app/CommercialCleanersBoard';
import { cleanersCcMenuItem } from '../components/sidebar/CleanersCCLink';
import UserMenu from '../components/UserMenu';
import NotificationsBell from '../components/NotificationsBell';
import AddEmployeeDialog from '../components/AddEmployeeDialog';
import { isCorporateEmployee, employeeScheduleLabel } from '../utils/corporateSchedules';
import BulkImportEmployeesDialog from '../components/BulkImportEmployeesDialog';
import OpsStaffingRoster from '../components/OpsStaffingRoster';
import OpsPropertiesView from '../components/OpsPropertiesView';
import { CrewLeaderboard } from '../components/staff/CrewLeaderboard';
import { BonusPanel } from '../components/staff/BonusPanel';
import OpsRegionalDrilldown from '../components/ops/OpsRegionalDrilldown';
import { MetroKindHeader } from '../components/shared/MetroKindHeader';
import ResidentApprovalsQueue from '../components/ResidentApprovalsQueue';  // still used inside CS Command Center
import { LeaderboardCard } from '../components/admin/LeaderboardCard';
import LiveRoutesMap from '../components/admin/LiveRoutesMap';
import AiAccuracyTrendCard from '../components/admin/AiAccuracyTrendCard';
import RouteAlertsPanel from '../components/admin/RouteAlertsPanel';
import AlertConfigCard from '../components/admin/AlertConfigCard';
import DemoSandboxBadge from '../components/common/DemoSandboxBadge';
import OpsFleetSafetyDrilldown from '../components/ops/OpsFleetSafetyDrilldown';
import AdminStatisticsDashboard from '../components/AdminStatisticsDashboard';
import IncidentReportForm from '../components/IncidentReportForm';
import SafetyIncidentList from '../components/SafetyIncidentList';
import OpsTrainingCompletions from '../components/OpsTrainingCompletions';
import AccessRequestsTab from '../components/AccessRequestsTab';
import { 
  Users, AlertTriangle, CheckCircle, Clock, LogOut, User, 
  Plus, Ban, Eye, Send, X, Calendar, FileText,
  UserPlus, UserX, Award, ArrowRightLeft, Camera,
  Trophy, Star, Shield, ShieldCheck, DollarSign, ClipboardList, Upload, Truck, QrCode, GraduationCap, LayoutDashboard, MapPin, Sparkles, BarChart3, CalendarOff
} from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import QRCodesTab from '../components/QRCodesTab';
import DailyPickupReportCard from '../components/DailyPickupReportCard';
import TrainingHub from '../components/TrainingHub';
import AdminUpdatesManager from '../components/updates/AdminUpdatesManager';
import StaffPerformancePanel from '../components/admin/StaffPerformancePanel';
import BillingHub from '../components/BillingHub';
import PhotoCapture from '../components/PhotoCapture';
import NECSLogo from '../components/NECSLogo';
import AdminViolationReportsTab from '../components/admin/AdminViolationReportsTab';
import AdminDailyReportsTab from '../components/admin/AdminDailyReportsTab';
import AdminDemoSeed from '../components/AdminDemoSeed';
import { ScopedDemoSuite } from '../components/admin/ScopedDemoSuite';
import SalesLeadsTab from '../components/admin/SalesLeadsTab';
import RoutesRegionsTab from '../components/RoutesRegionsTab';
// (Iter-137 · Section 8) — Onboarding checklist moved from Operations
// CC to Customer Service CC. Import stays in place because CustomerServiceCC
// (rendered from DeptCommandCenters) receives the compact list variant.
import AdminBlackoutDates from '../components/AdminBlackoutDates';
import EmergencyBroadcastPanel from '../components/admin/EmergencyBroadcastPanel';
import { ManualOutageControl } from '../components/admin/ManualOutageControl';
import UberTrashDashboard from '../components/UberTrashDashboard';
import AdminBulkPickup from '../components/AdminBulkPickup';
import AdminSpecialtyServices from '../components/AdminSpecialtyServices';
import AdminHome from '../components/AdminHome';
import MessagesHealthChip from '../components/admin/MessagesHealthChip';
// Iter-180 · Phase 2 — AdminControlStrip removed from CompanyManagerDashboard.
import AdminRoutesRegions from '../components/AdminRoutesRegions';
import LiveTrackingMap from '../components/LiveTrackingMap';
import AdminSupportChat from '../components/AdminSupportChat';
import BrokersTab from '../components/BrokersTab';
import TabHelp from '../components/TabHelp';
import {
  NECS_DEPARTMENTS,
  HomeAdministrationView,
  DepartmentLandingView,
  OperationsHeader,
} from '../components/admin/AdminDepartments';
import ExecutiveCommandCenter from '../components/admin/ExecutiveCommandCenter';
import HRCommandCenter from '../components/admin/HRCommandCenter';
import ITCommandCenter from '../components/admin/ITCommandCenter';
import ContractsTab from '../components/dashboard/tabs/ContractsTab';
import {
  FinanceCommandCenter, LegalCommandCenter, SalesCommandCenter,
  MarketingCommandCenter, CustomerServiceCommandCenter, ProcurementCommandCenter,
  SafetyCommandCenter, TrainingCommandCenter,
} from '../components/admin/DeptCommandCenters';
import AttendanceTab from '../components/AttendanceTab';
import { ServiceSignalsLegend } from '../components/ServiceSignalsLegend';
import { useProfilePhotoSync } from '../hooks/useProfilePhotoSync';
import OnboardingTour, { shouldShowTour } from '../components/admin/OnboardingTour';
import { canAccessDepartment, lockReasonFor } from '../utils/departmentAccess';
import { Lock, UserCog } from 'lucide-react';
import { STAFF_SHIFTS, STAFF_SHIFT_ORDER, shiftLabel, DEFAULT_STAFF_SHIFT } from '../utils/staffShifts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SHIFTS = STAFF_SHIFTS;

const PRICING_TEMPLATES = {
  quoted: "Thank you for your request. We have prepared a pricing quote for your property.",
  needs_info: "Thank you for your request. We need a few more details before finalizing pricing.",
  approved: "Good news! Your pricing request has been approved. We can begin onboarding at your convenience."
};

// Sections that no longer belong to the Operations top-tab bar — they
// render via the Quick Access strip on their owning department Command
// Center only (per the Feb 2026 CEO ownership directive).
const REASSIGNED_SECTIONS = ['employees', 'attendance', 'blackouts', 'pricing', 'contracts', 'billing', 'training', 'demo-tools', 'brokers', 'ops-properties', 'statistics', 'fleet-safety', 'sales-leads', 'ops-staffing', 'support-chat', 'live-tracking', 'specialty-services', 'reports'];

export default function CompanyManagerDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, loading, logout } = useAuth();
  useProfilePhotoSync(setUser);
  
  const [activeTab, setActiveTab] = useState('home');
  const [expandedDept, setExpandedDept] = useState(null);
  // Iter-85 · CEO / admin lands on the Executive Command Center by default
  // so the strategy scorecards (Sales/Finance/Safety/HR/Risk/Growth) are
  // the FIRST thing they see. Left-menu department nav still switches views.
  const [activeDept, setActiveDept] = useState('home');
  // Iter-111 · Fix 2 — Enterprise Pivot Filter instance lifted to the
  // parent. One instance per department board (boardId keyed by dept),
  // passed down as a prop so board fetches re-run on every change.
  const [parentDept, setParentDept] = useState(null);
  const deptBoardFilter = useBoardFilter(`dept-${parentDept || activeDept}`, { sharedAdmin: true });
  const scopeTerms = adminScopeTerms(deptBoardFilter.view_as);
  // Iter-104 · Item 2 — View As toggle state now lives in the unified
  // filter context (`view_as`). Local state removed to avoid drift.
  const [specialtyServiceType, setSpecialtyServiceType] = useState(null);
  const [showTour, setShowTour] = useState(false);
  // Deep-link support — /company-manager-dashboard?tab=sales-leads jumps
  // straight into the Operations Sales Leads tab (used by the new-lead
  // email notifications sent from routes/leads.py).
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const tab = params.get('tab');
    if (params.get('dept') === 'customer-service') {
      setActiveDept('customer-service');
      return;
    }
    if (tab) {
      setActiveDept('operations');
      setActiveTab(tab);
    }
  }, [location.search]);
  // Tracks which department "owns" the current Operations-hosted section
  // (Employees, Attendance, Blackouts, Billing, etc.). Set when a Quick
  // Access tile is clicked from HR / Finance / Legal / etc., so we can
  // render a "Back to [Dept] Command Center" breadcrumb and hide the
  // Operations stats cards while the reassigned section is visible.
  // Tracks whether the current cross-department jump is view-only (e.g.
  // Training → Staffing Agents is read-only). Tab content components
  // can read this via the `data-parent-readonly` attribute on the shell
  // and hide edit/delete buttons accordingly.
  const [parentReadOnly, setParentReadOnly] = useState(false);
  // Handler passed to every Command Center — flips to Operations context
  // with the section active and remembers where we came from + whether
  // we came in read-only mode.
  const onOpenDeptSection = (fromDept) => (sectionKey, opts) => {
    setParentDept(fromDept);
    setParentReadOnly(!!(opts && opts.readOnly));
    setActiveDept('operations');
    setActiveTab(sectionKey);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Show the 10-second onboarding tour exactly once per admin, on first
  // login. Triggers after the Home view has mounted so the targets exist.
  useEffect(() => {
    if (activeDept !== 'home') return;
    if (!user) return;
    if (!shouldShowTour(user)) return;
    const t = setTimeout(() => setShowTour(true), 600);
    return () => clearTimeout(t);
  }, [activeDept, user]);
  const [pendingViolations, setPendingViolations] = useState([]);
  const [allViolations, setAllViolations] = useState([]);
  const [employees, setEmployees] = useState([]);
  // Big City tab strip filter for Employee Management (Feb 2026 CEO
  // directive) — scopes the Active / Blocked / Terminated roster to
  // the picked metro. Default Dallas since it's the launch metro.
  const [empBigCity, setEmpBigCity] = useState('Dallas');
  // Shared metro scope for the non-Ops tabs (Training, Billing,
  // Contracts, Blackouts, Pricing, Weekly …). One source of truth so
  // the CEO picks Houston once and every command-center tab honours
  // it via the shared MetroKindHeader chrome. Feb 2026 PhD directive.
  const [companyMetro, setCompanyMetro] = useState('Dallas');
  const [companyMetroKind, setCompanyMetroKind] = useState('all');
  // Admin/Field kind toggle — matches the Attendance page pattern so
  // JK can hide one population entirely (e.g. only field staff, no
  // corporate) while still seeing all 3 lifecycle buckets below.
  const [empKind, setEmpKind] = useState('all'); // all · admin · staff
  const [dailySummary, setDailySummary] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [swapRequests, setSwapRequests] = useState([]);
  const [weeklyReport, setWeeklyReport] = useState(null);
  const [staffGallery, setStaffGallery] = useState([]);
  const [dailyReports, setDailyReports] = useState([]);
  const [safetyReports, setSafetyReports] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [eligibleDrivers, setEligibleDrivers] = useState([]);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  // 'choose' → step 1 (pick NECS Admin vehicle OR Staff/Field vehicle)
  // 'admin' / 'staff' → step 2 form
  const [vehicleStep, setVehicleStep] = useState('choose');
  const [vehicleForm, setVehicleForm] = useState({
    fleet_kind: 'staff',
    driver_id: '',
    driver_name: '',
    department: '',
    make: '',
    model: '',
    mileage: '',
    license_plate: '',
    position: '',
    role: '',
    state: '',
    region: '',
  });
  const [contracts, setContracts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [contractForm, setContractForm] = useState({
    property_id: '',
    file: null
  });
  const [uploadingContract, setUploadingContract] = useState(false);
  const [regions, setRegions] = useState([]);
  const [pricingRequests, setPricingRequests] = useState([]);
  const [activePricingRequest, setActivePricingRequest] = useState(null);
  const [pricingResponse, setPricingResponse] = useState({
    status: 'quoted',
    response_message: '',
    price_amount: '',
    price_frequency: 'per unit/month',
    price_notes: '',
    contract_start_date: '',
    contract_term_years: 1,
    contract_end_date: '',
    send_email: true,
    send_sms: false
  });
  const [pricingFilters, setPricingFilters] = useState({
    status: 'all',
    startDate: '',
    endDate: ''
  });
  const [reminderSettings, setReminderSettings] = useState({
    send_email: true,
    send_sms: false
  });
  
  // Dialogs
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showViolationDetail, setShowViolationDetail] = useState(null);
  const [showAddSchedule, setShowAddSchedule] = useState(false);
  const [showWeeklyReport, setShowWeeklyReport] = useState(false);
  
  // Forms
  const [newEmployee, setNewEmployee] = useState({
    email: '',
    name: '',
    phone: '',
    role: 'agent',
    assigned_shift: DEFAULT_STAFF_SHIFT,
    profile_photo: null
  });
  const [forwardNotes, setForwardNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [scheduleForm, setScheduleForm] = useState({
    user_id: '',
    date: '',
    shift_type: DEFAULT_STAFF_SHIFT,
    property_ids: []
  });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate('/login');
      return;
    }

    if (!user && location.state?.user) {
      setUser(location.state.user);
    }

    if (user?.role && !['company_manager', 'admin'].includes(user.role)) {
      // Redirect based on role
      if (user.role === 'tenant') navigate('/tenant-dashboard');
      else if (user.role === 'property_manager') navigate('/property-manager-dashboard');
      else navigate('/staff-dashboard');
    }
  }, [user, loading, navigate, location.state, setUser]);

  useEffect(() => {
    if (activeDept === 'operations' && user && ['company_manager', 'admin'].includes(user.role)) {
      setPendingViolations([]); setAllViolations([]); setEmployees([]);
      setProperties([]); setContracts([]); setDailySummary(null);
      setSchedules([]); setSwapRequests([]); setDailyReports([]); setSafetyReports([]);
      fetchPendingViolations();
      fetchAllViolations();
      fetchEmployees();
      fetchDailySummary();
      fetchNotifications();
      fetchSchedules();
      fetchSwapRequests();
      fetchWeeklyReport();
      fetchStaffGallery();
      fetchPricingRequests();
      fetchDailyReports();
      fetchSafetyReports();
      fetchVehicles();
      fetchContracts();
      fetchProperties();
      fetchRegions();
    }
  }, [user, activeDept, deptBoardFilter.queryString, deptBoardFilter.refreshTick]);

  const fetchPendingViolations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/violations/pending-review`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPendingViolations(data);
      }
    } catch (error) {
      console.error('Failed to fetch pending violations:', error);
    }
  };

  const fetchAllViolations = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/violations`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAllViolations(data);
      }
    } catch (error) {
      console.error('Failed to fetch violations:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/company/employees`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      }
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/schedules?date=${selectedDate}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSchedules(data);
      }
    } catch (error) {
      console.error('Failed to fetch schedules:', error);
    }
  };

  const fetchSwapRequests = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/swap-requests`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSwapRequests(data);
      }
    } catch (error) {
      console.error('Failed to fetch swap requests:', error);
    }
  };

  const fetchWeeklyReport = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/weekly-report`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWeeklyReport(data);
      }
    } catch (error) {
      console.error('Failed to fetch weekly report:', error);
    }
  };

  const fetchStaffGallery = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/staff/gallery`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setStaffGallery(data);
      }
    } catch (error) {
      console.error('Failed to fetch staff gallery:', error);
    }
  };

  const fetchPricingRequests = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pricing-requests`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPricingRequests(data);
      }
    } catch (error) {
      console.error('Failed to fetch pricing requests:', error);
    }
  };

  const fetchDailyReports = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/daily-reports`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDailyReports(data);
      }
    } catch (error) {
      console.error('Failed to fetch daily reports:', error);
    }
  };

  const fetchSafetyReports = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicle-safety`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setSafetyReports(data);
      }
    } catch (error) {
      console.error('Failed to fetch safety reports:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setVehicles(data);
      }
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
    }
  };

  // Eligible drivers — admins (CEO/Director/Director Assistant/Manager/Supervisor)
  // and site agents (Site Agent/Manager/Supervisor/Crew Leader). The Add
  // Vehicle dialog filters this list by the chosen fleet kind ('admin'
  // vs 'staff') so each sheet only shows the relevant party. Selecting
  // a driver auto-fills department / role / position / state / region.
  const fetchEligibleDrivers = async (kind) => {
    try {
      const qs = kind ? `?fleet_kind=${kind}` : '';
      const response = await fetch(`${BACKEND_URL}/api/vehicles/eligible-employees${qs}`, {
        credentials: 'include'
      });
      if (response.ok) setEligibleDrivers(await response.json());
    } catch (e) {
      console.error('Failed to fetch eligible drivers:', e);
    }
  };

  // Cascading filters for the Add Vehicle "Choose driver" flow.
  // Admin selects Role + Position first, then the Full Name dropdown
  // narrows down to the matching HR employees. On name pick, badge /
  // region / city / state / assigned shift / role type auto-fill from
  // the Human Resources sheet so admin only enters the vehicle info.
  const [vehicleFilters, setVehicleFilters] = useState({ role: '', position: '', department: '', state: '', big_city: '', region: '' });

  const openVehicleSheet = (kind) => {
    setVehicleForm({
      fleet_kind: kind, driver_id: '', driver_name: '', department: '',
      make: '', model: '', mileage: '', license_plate: '',
      position: '', role: '', state: '', region: '', city: '',
      badge_number: '', role_type: '', assigned_shift: '',
    });
    setVehicleFilters({ role: '', position: '', department: '', state: '', big_city: '', region: '' });
    setVehicleStep(kind);
    fetchEligibleDrivers(kind);
  };

  // Normalize position/department/role strings so the strict compare in
  // filteredDrivers survives seed data that uses raw HR text
  // ("Director", "Operations", "Site Agent", …) versus the enum-style
  // Select values the CEO spec'd ("director", "operations", "agent").
  const norm = (v) => (v || '')
    .toString()
    .toLowerCase()
    .replace(/[-\s/]+/g, '_')
    .replace(/[^a-z0-9_]/g, '');

  // Synonym map — collapses common HR shorthand into the canonical
  // Select value so 'Site Agent' matches Position='agent', etc.
  const POSITION_SYNONYMS = {
    site_agent: 'agent',
    field_agent: 'agent',
    trash_agent: 'agent',
    lead: 'crew_leader',
    crewleader: 'crew_leader',
    team_lead: 'crew_leader',
    site_supervisor: 'supervisor',
    ops_supervisor: 'supervisor',
    regional_supervisor: 'supervisor',
    executive_csuite: 'ceo',
    executive_c_suite: 'ceo',
    csuite: 'ceo',
    directorassistant: 'director_assistant',
    asst_director: 'director_assistant',
  };
  const canon = (v) => {
    const n = norm(v);
    return POSITION_SYNONYMS[n] || n;
  };

  // Build a region_code → region_id lookup so both id and code compares match
  const regionCodeById = (regions || []).reduce((acc, r) => {
    if (r.region_id) acc[r.region_id] = r.region_code || null;
    return acc;
  }, {});

  const filteredDrivers = eligibleDrivers.filter((d) => {
    if (vehicleFilters.role && norm(d.role) !== norm(vehicleFilters.role)) return false;
    if (vehicleFilters.position && canon(d.position) !== canon(vehicleFilters.position)) return false;
    if (vehicleFilters.department && norm(d.department) !== norm(vehicleFilters.department)) return false;
    if (vehicleFilters.state && (d.state || '').toUpperCase() !== vehicleFilters.state) return false;
    // Big City filter — resolve via the employee's region → big_city
    // relationship. Employees with no region on file are excluded once
    // a specific big city is picked so ops isn't served stale matches.
    if (vehicleFilters.big_city) {
      const empRegion = (regions || []).find(
        (r) => r.region_id === (d.region_id || d.region) || r.region_code === (d.region_code || d.region),
      );
      if (!empRegion || (empRegion.big_city || 'Unassigned') !== vehicleFilters.big_city) return false;
    }
    if (vehicleFilters.region) {
      // Accept both direct region_id match AND region_code cross-match
      const pickedCode = regionCodeById[vehicleFilters.region];
      const empKey = d.region_id || d.region || '';
      if (empKey !== vehicleFilters.region && empKey !== pickedCode) return false;
    }
    return true;
  });

  // Auto-populate the Full Name when the Step 1 filter selections narrow
  // down to a single HR-approved employee. Ops agents shouldn't have to
  // hunt through a dropdown when they've already specified enough to
  // identify the target person. HR remains the sole source of the
  // registration itself — this only surfaces the match.
  useEffect(() => {
    if (!showAddVehicle) return;
    if (filteredDrivers.length === 1 && vehicleForm.driver_id !== filteredDrivers[0].user_id) {
      handleSelectDriver(filteredDrivers[0].user_id);
    }
  }, [vehicleFilters, showAddVehicle]);
  const driverRoleOptions = Array.from(new Set(eligibleDrivers.map((d) => d.role).filter(Boolean)));
  const driverPositionOptions = Array.from(
    new Set(
      eligibleDrivers
        .filter((d) => !vehicleFilters.role || d.role === vehicleFilters.role)
        .map((d) => d.position)
        .filter(Boolean)
    )
  );

  const handleSelectDriver = (driverId) => {
    // Guard against the disabled "__no_match__" sentinel and empty pick.
    if (!driverId || driverId === '__no_match__') return;
    const d = eligibleDrivers.find((x) => x.user_id === driverId);
    if (!d) {
      setVehicleForm((f) => ({ ...f, driver_id: '', driver_name: '' }));
      return;
    }
    // Pull every HR-relevant field so the admin doesn't have to retype
    // anything. NOTE: `hr_state` is the employee's HR-sourced state
    // (shown in the Step 2 snapshot); `state` is reserved for the
    // vehicle's PLATE state which Ops picks explicitly in Step 3 —
    // we do NOT seed it from HR so Ops must consciously choose.
    setVehicleForm((f) => ({
      ...f,
      driver_id: d.user_id,
      driver_name: d.name || '',
      department: d.department || '',
      position: d.position || '',
      role: d.role || '',
      hr_state: d.state || '',
      state: f.state || '',
      region: d.region || '',
      city: d.city || '',
      badge_number: d.badge_number || '',
      role_type: d.role_type || '',
      assigned_shift: d.assigned_shift || '',
    }));
  };

  const fetchContracts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/contracts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data);
      }
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
    }
  };

  const fetchProperties = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/properties`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setProperties(data);
      }
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/routes/regions`, {
        credentials: 'include'
      });
      if (response.ok) setRegions(await response.json());
    } catch (_) { /* silent */ }
  };

  const fetchDailySummary = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/company/daily-summary`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDailySummary(data);
      }
    } catch (error) {
      console.error('Failed to fetch daily summary:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...vehicleForm,
        mileage: vehicleForm.mileage ? parseInt(vehicleForm.mileage, 10) : null,
        make_model: `${vehicleForm.make || ''} ${vehicleForm.model || ''}`.trim(),
      };
      const response = await fetch(`${BACKEND_URL}/api/vehicles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      if (response.ok) {
        toast.success('Vehicle added successfully');
        setShowAddVehicle(false);
        setVehicleStep('choose');
        setVehicleForm({
          fleet_kind: 'staff', driver_id: '', driver_name: '', department: '', make: '', model: '',
          mileage: '', license_plate: '', position: '', role: '', state: '', region: '',
        });
        fetchVehicles();
        fetchEmployees();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add vehicle');
      }
    } catch (error) {
      toast.error('Failed to add vehicle');
    }
  };

  const handleUpdateVehicleStatus = async (vehicleId, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/vehicles/${vehicleId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        toast.success('Vehicle status updated');
        fetchVehicles();
      } else {
        toast.error('Failed to update vehicle status');
      }
    } catch (error) {
      toast.error('Failed to update vehicle status');
    }
  };

  const handleAddEmployee = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/company/employees`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newEmployee)
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Employee ${data.employee.name} created! Badge: ${data.employee.badge_number}`);
        toast.info(`Temporary password: ${data.employee.temp_password}`, { duration: 10000 });
        setShowAddEmployee(false);
        setNewEmployee({ email: '', name: '', phone: '', role: 'agent', assigned_shift: DEFAULT_STAFF_SHIFT, profile_photo: null });
        fetchEmployees();
        fetchStaffGallery();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create employee');
      }
    } catch (error) {
      toast.error('Failed to create employee');
    }
  };

  const handleOpenPricingResponse = (request) => {
    setActivePricingRequest(request);
    setPricingResponse({
      status: request.status === 'submitted' ? 'quoted' : request.status,
      response_message: request.response_message || '',
      price_amount: request.price_amount || '',
      price_frequency: request.price_frequency || 'per unit/month',
      price_notes: request.price_notes || '',
      contract_start_date: request.contract_start_date || '',
      contract_term_years: request.contract_term_years || 1,
      contract_end_date: request.contract_end_date || '',
      send_email: true,
      send_sms: false
    });
  };

  const handlePricingStatusChange = (value) => {
    setPricingResponse((prev) => ({
      ...prev,
      status: value,
      response_message: prev.response_message || PRICING_TEMPLATES[value] || ''
    }));
  };

  const computeContractEndDate = (startDate, termYears) => {
    if (!startDate || !termYears) return '';
    const start = new Date(startDate);
    if (Number.isNaN(start.getTime())) return '';
    const end = new Date(start);
    end.setFullYear(end.getFullYear() + Number(termYears));
    return end.toISOString().slice(0, 10);
  };

  const updateContractTerm = (updates) => {
    setPricingResponse((prev) => {
      const next = { ...prev, ...updates };
      next.contract_end_date = computeContractEndDate(next.contract_start_date, next.contract_term_years);
      return next;
    });
  };

  const applyPricingTemplate = (value) => {
    setPricingResponse((prev) => ({
      ...prev,
      status: value,
      response_message: PRICING_TEMPLATES[value] || ''
    }));
  };

  const handleSubmitPricingResponse = async (e) => {
    e.preventDefault();
    if (!activePricingRequest) return;

    const payload = {
      ...pricingResponse,
      price_frequency: pricingResponse.price_amount ? pricingResponse.price_frequency : '',
      contract_end_date: pricingResponse.contract_end_date || computeContractEndDate(pricingResponse.contract_start_date, pricingResponse.contract_term_years)
    };

    try {
      const response = await fetch(`${BACKEND_URL}/api/pricing-requests/${activePricingRequest.request_id}/respond`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send response');
      }

      toast.success('Pricing response sent to Property Manager');
      setActivePricingRequest(null);
      fetchPricingRequests();
    } catch (error) {
      toast.error(error.message || 'Failed to send response');
    }
  };

  const handleSendRenewalReminder = async (requestId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/pricing-requests/${requestId}/remind`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(reminderSettings)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send reminder');
      }

      const data = await response.json();
      toast.success(`Renewal reminder sent (${data.days_remaining} days remaining)`);
      fetchPricingRequests();
    } catch (error) {
      toast.error(error.message || 'Failed to send reminder');
    }
  };

  const formatPricingDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-US');
  };

  const getFilteredPricingRequests = () => {
    return pricingRequests.filter((request) => {
      if (pricingFilters.status !== 'all' && request.status !== pricingFilters.status) {
        return false;
      }

      const createdAt = request.created_at ? new Date(request.created_at) : null;
      if (pricingFilters.startDate) {
        const start = new Date(`${pricingFilters.startDate}T00:00:00`);
        if (createdAt && createdAt < start) return false;
      }
      if (pricingFilters.endDate) {
        const end = new Date(`${pricingFilters.endDate}T23:59:59`);
        if (createdAt && createdAt > end) return false;
      }
      return true;
    });
  };

  const getContractDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    if (Number.isNaN(end.getTime())) return null;
    const today = new Date();
    const diff = Math.ceil((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const exportPricingCSV = () => {
    const filteredRequests = getFilteredPricingRequests();
    if (!filteredRequests.length) {
      toast.info('No pricing requests to export');
      return;
    }

    const headers = [
      'Request ID',
      'Property',
      'Package',
      'Contact Name',
      'Email',
      'Phone',
      'Status',
      'Price Amount',
      'Price Frequency',
      'Price Notes',
      'Contract Start',
      'Contract End',
      'Contract Term (Years)',
      'Response Message',
      'Submitted Date',
      'Responded Date'
    ];

    const sanitize = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value).replace(/"/g, '""');
      return `"${str}"`;
    };

    const rows = filteredRequests.map((request) => ([
      request.request_id,
      request.property_name || '',
      request.package_type || '',
      request.contact_name || '',
      request.contact_email || '',
      request.contact_phone || '',
      request.status || '',
      request.price_amount || '',
      request.price_frequency || '',
      request.price_notes || '',
      request.contract_start_date || '',
      request.contract_end_date || '',
      request.contract_term_years || '',
      request.response_message || '',
      formatPricingDate(request.created_at),
      formatPricingDate(request.responded_at)
    ].map(sanitize)));

    const csvContent = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pricing-requests-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportPricingPDF = () => {
    const filteredRequests = getFilteredPricingRequests();
    if (!filteredRequests.length) {
      toast.info('No pricing requests to export');
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFontSize(16);
    doc.text('Noble Empire Pricing Requests', pageWidth / 2, y, { align: 'center' });
    y += 10;

    doc.setFontSize(10);
    filteredRequests.forEach((request, index) => {
      const details = [
        `Request ID: ${request.request_id}`,
        `Property: ${request.property_name || '—'}`,
        `Package: ${request.package_type || '—'}`,
        `Contact: ${request.contact_name || '—'} (${request.contact_email || '—'}) ${request.contact_phone ? `| ${request.contact_phone}` : ''}`,
        `Status: ${request.status || '—'}`,
        `Pricing: ${request.price_amount || '—'} ${request.price_frequency || ''}`,
        `Contract: ${request.contract_start_date || '—'} to ${request.contract_end_date || '—'} (${request.contract_term_years || '—'} years)`,
        `Notes: ${request.price_notes || '—'}`,
        `Response: ${request.response_message || '—'}`,
        `Submitted: ${formatPricingDate(request.created_at)} | Responded: ${formatPricingDate(request.responded_at)}`
      ];

      const text = doc.splitTextToSize(details.join('\n'), pageWidth - 20);
      if (y + text.length * 6 > 280) {
        doc.addPage();
        y = 20;
      }

      doc.text(`Request ${index + 1}`, 10, y);
      y += 6;
      doc.text(text, 10, y);
      y += text.length * 6 + 8;
    });

    doc.save(`pricing-requests-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const handleContractUpload = async (e) => {
    e.preventDefault();
    if (!contractForm.property_id || !contractForm.file) {
      toast.error('Select a property and upload a contract file');
      return;
    }

    const formData = new FormData();
    formData.append('property_id', contractForm.property_id);
    formData.append('file', contractForm.file);

    try {
      setUploadingContract(true);
      const response = await fetch(`${BACKEND_URL}/api/contracts/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload contract');
      }

      toast.success('Contract uploaded');
      setContractForm({ property_id: '', file: null });
      fetchContracts();
    } catch (error) {
      toast.error(error.message || 'Failed to upload contract');
    } finally {
      setUploadingContract(false);
    }
  };

  const handleCreateSchedule = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(scheduleForm)
      });

      if (response.ok) {
        toast.success('Schedule created successfully');
        setShowAddSchedule(false);
        setScheduleForm({ user_id: '', date: '', shift_type: DEFAULT_STAFF_SHIFT, property_ids: [] });
        fetchSchedules();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create schedule');
      }
    } catch (error) {
      toast.error('Failed to create schedule');
    }
  };

  const handleApproveSwap = async (swapId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/swap-requests/${swapId}/approve-manager`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Swap request approved');
        fetchSwapRequests();
        fetchSchedules();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to approve swap');
      }
    } catch (error) {
      toast.error('Failed to approve swap');
    }
  };

  const handleRejectSwap = async (swapId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/swap-requests/${swapId}/reject?reason=Denied by management`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Swap request rejected');
        fetchSwapRequests();
      }
    } catch (error) {
      toast.error('Failed to reject swap');
    }
  };

  const handleArchiveWeeklyReport = async () => {
    if (!weeklyReport?.week_start) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/weekly-report/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ week_start: weeklyReport.week_start })
      });

      if (response.ok) {
        toast.success('Weekly report archived for bonus processing');
      }
    } catch (error) {
      toast.error('Failed to archive report');
    }
  };

  const handleToggleBlock = async (employee) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/company/employees/${employee.user_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_blocked: !employee.is_blocked })
      });

      if (response.ok) {
        toast.success(`Employee ${employee.is_blocked ? 'unblocked' : 'blocked'} successfully`);
        fetchEmployees();
      }
    } catch (error) {
      toast.error('Failed to update employee');
    }
  };

  // Terminate / reinstate — the third bucket in Employee Management.
  // Terminated employees are ex-NECS and are hidden from active
  // dashboards; reinstate flips them back to Active.
  const handleToggleTerminate = async (employee) => {
    const terminating = !employee.is_terminated;
    let reason = null;
    if (terminating) {
      reason = window.prompt(
        `Terminate ${employee.name}?\nOptional termination reason:`,
        '',
      );
      if (reason === null) return; // user cancelled
    } else if (!window.confirm(`Reinstate ${employee.name} to Active roster?`)) {
      return;
    }
    try {
      const response = await fetch(`${BACKEND_URL}/api/company/employees/${employee.user_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          is_terminated: terminating,
          termination_reason: terminating ? (reason || null) : null,
        }),
      });
      if (response.ok) {
        toast.success(`Employee ${terminating ? 'terminated' : 'reinstated'} successfully`);
        fetchEmployees();
      } else {
        toast.error('Failed to update employee');
      }
    } catch (_e) {
      toast.error('Failed to update employee');
    }
  };

  const handleImpersonateEmployee = async (employee) => {
    if (!window.confirm(`Step into ${employee.name}'s session?\n\nYour admin session will be paused. Use the gold banner at the top of the screen to return any time.`)) {
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/company/employees/${employee.user_id}/impersonate`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.detail || 'Could not impersonate employee.');
        return;
      }
      toast.success(`Now signed in as ${employee.name}`);
      const role = data.user?.role;
      const dest =
        role === 'property_manager' ? '/property-manager-dashboard' :
        ['manager', 'regional_manager', 'supervisor', 'crew_leader', 'agent', 'site_agent', 'staff'].includes(role) ? '/staff-dashboard' :
        role === 'tenant' ? '/tenant-dashboard' : '/';
      window.location.href = dest;
    } catch (err) {
      toast.error('Network error while impersonating.');
    }
  };

  const handleForwardViolation = async (violationId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/violations/${violationId}/forward`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: forwardNotes })
      });

      if (response.ok) {
        toast.success('Violation forwarded to Property Manager');
        setShowViolationDetail(null);
        setForwardNotes('');
        fetchPendingViolations();
        fetchAllViolations();
      }
    } catch (error) {
      toast.error('Failed to forward violation');
    }
  };

  const handleRejectViolation = async (violationId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/violations/${violationId}/reject?reason=${encodeURIComponent(rejectReason || 'Insufficient evidence')}`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Violation report rejected');
        setShowViolationDetail(null);
        setRejectReason('');
        fetchPendingViolations();
        fetchAllViolations();
      }
    } catch (error) {
      toast.error('Failed to reject violation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#2D2D2D' }}>
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#C9A962' }}></div>
          <p className="mt-4 text-white">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  // Map every region_code (R01, H1, CT2, ST3, ET1, …) to its big city
  // so we can bucket employees by metro purely on the frontend.
  const bigCityByRegionCode = {};
  (regions || []).forEach((r) => {
    if (r.region_code) bigCityByRegionCode[r.region_code] = r.big_city || null;
    if (r.region_id) bigCityByRegionCode[r.region_id] = r.big_city || null;
  });

  const filterByBigCity = (list) => list.filter((e) => {
    // Un-regioned employees stay visible under every metro so ops can
    // still see corporate/admin staff who don't map to a field zone.
    if (!e.region_code && !e.region_id) return true;
    const bc = bigCityByRegionCode[e.region_code] || bigCityByRegionCode[e.region_id];
    return !bc || bc === empBigCity;
  });

  // Split each roster into Admin (corporate — admin_office shift) and
  // Field Staff (everyone else) so the CEO can eyeball the corporate
  // vs field mix at a glance without expanding filters.  Honors the
  // top-level empKind toggle: 'admin' keeps only admins in both
  // buckets, 'staff' keeps only field staff, 'all' returns both.
  const splitAdminStaff = (list) => {
    const admin = list.filter(isCorporateEmployee);
    const staff = list.filter((e) => !isCorporateEmployee(e));
    return {
      admin: empKind === 'staff' ? [] : admin,
      staff: empKind === 'admin' ? [] : staff,
    };
  };

  // Rehire eligibility hint — surfaces ex-agents terminated more than
  // 180 days ago for reasons other than misconduct. Recruiters can
  // fast-track them back during peak-season staffing crunches.
  const rehireEligible = (emp) => {
    if (!emp.is_terminated || !emp.terminated_at) return false;
    const reason = String(emp.termination_reason || '').toLowerCase();
    if (reason.includes('misconduct') || reason.includes('theft') || reason.includes('fraud')) return false;
    const days = (Date.now() - new Date(emp.terminated_at).getTime()) / (1000 * 60 * 60 * 24);
    return days >= 180;
  };

  const activeEmployees = filterByBigCity(employees.filter((e) => !e.is_blocked && !e.is_terminated));
  const blockedEmployees = filterByBigCity(employees.filter((e) => e.is_blocked && !e.is_terminated));
  const terminatedEmployees = filterByBigCity(employees.filter((e) => e.is_terminated));
  const pendingPricingRequests = pricingRequests.filter(request => request.status === 'submitted');
  const filteredPricingRequests = getFilteredPricingRequests();

  return (
    <>
    <DemoSandboxBadge />
    <DepartmentNavigationProvider activeDept={parentDept || activeDept} onNavigate={dept => {
      if (!canAccessDepartment(user, dept)) return;
      setParentDept(null); setParentReadOnly(false); setActiveDept(dept); setActiveTab('home');
    }}>
    <AdminBoardFilterContext.Provider value={deptBoardFilter}>
    <DashboardShell
      brandLabel="NECS Admin"
      menuItems={[
        ...NECS_DEPARTMENTS
          .filter((d) => !user?.admin_department || canAccessDepartment(user, d.key))
          .map((d) => {
            const accessible = canAccessDepartment(user, d.key);
            return {
              key: d.key,
              label: d.label,
              icon: accessible ? d.icon : Lock,
            };
          }),
        // Iter-105 · Item 4 — Single unified Cleaners CC sidebar entry
        // (role-gate lives inside `cleanersCcMenuItem`). Returns null
        // for roles that should not see the link.
      ].filter(Boolean)}
      activeKey={parentDept || activeDept}
      onMenuClick={(item) => {
        const accessible = canAccessDepartment(user, item.key);
        if (!accessible) {
          toast.error(lockReasonFor(user, item.key));
          return;
        }
        setParentDept(null);
        setActiveTab('home');
        setActiveDept(item.key);
        if (location.search) navigate('/company-manager-dashboard');
      }}
      hideUserMenu
      topbarExtras={(
        <button
          type="button"
          onClick={logout}
          className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:opacity-90 transition"
          style={{ backgroundColor: '#000000', color: '#C9A962', border: '1px solid #2D2D2D' }}
          aria-label="Sign out"
          data-testid="topbar-logout-button"
          title="Sign out"
        >
          <LogOut className="h-4 w-4" />
        </button>
      )}
    >
    {showTour && <OnboardingTour user={user} onClose={() => setShowTour(false)} />}
    <div className="min-h-full text-white" style={{ backgroundColor: '#2D2D2D' }} data-testid="company-manager-dashboard">
      {/* Iter-110 · Item 2 — one-time 2FA push banner for leadership roles. */}
      <TwoFABanner />
      {/* Profile navbar — visible on every department view (Home, Operations,
          and all department landings). Gold bottom border is pixel-aligned
          with the sidebar logo case bottom border. */}
      <nav className="sticky top-[49px] sm:top-[65px] z-20" style={{ backgroundColor: '#3D3D3D' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center gap-2 min-h-20 relative">
            <div className="flex items-center shrink-0">
              <Badge data-testid="admin-topbar-brand" style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}>NECS ADMIN</Badge>
            </div>
            <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center" />
            <div className="flex items-center justify-end gap-2 sm:gap-4 min-w-0 ml-auto">
              {/* Iter-176 · Phase 8 — Messages Health chip lives in the
                  persistent admin topbar so it renders regardless of
                  active department. Auto-hides on non-admin roles. */}
              <MessagesHealthChip />
              {/* Iter-180 · Phase 1 — Admin cluster: UserMenu → Bell (no Trophy). */}
              <div data-testid="topbar-user-menu-wrap">
                <UserMenu />
              </div>
              <div data-testid="topbar-bell-wrap">
                <NotificationsBell variant="dark" />
              </div>
            </div>
          </div>
        </div>
      </nav>
      {activeDept !== 'home' && user?.admin_department && <div className="border-b border-[#C9A962]/40 px-4 sm:px-6 py-3 text-sm text-[#C9A962] break-words" data-testid="admin-delegation-identity">
        {user.department || user.admin_department} · {user.ops_division ? `${user.ops_division} · ` : ''}{user.position || user.admin_position}
      </div>}

      {/* Iter-180 · Phase 2 — Duplicate Admin Control Strip removed.
          The persistent filter board that already lives under the
          Department Presentation / Roles Keys tabs is the source of
          truth for admin filtering. */}

      {activeDept === 'home' && (
        <>
        <HomeAdministrationView
          minimal
          boardFilter={deptBoardFilter}
          user={user}
          userDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null}
          canAccess={(k) => canAccessDepartment(user, k)}
          onJump={(k) => {
            if (!canAccessDepartment(user, k)) {
              toast.error(lockReasonFor(user, k));
              return;
            }
            setActiveDept(k);
            setParentDept(null);
          }}
        />
        </>
      )}

      {/* Iter-111 · Fix 1 — the Enterprise Pivot Filter is no longer
          mounted here (above the boards). Each command center now renders
          it below its department header, role assignment and key
          responsibilities via the lifted `deptBoardFilter` prop. */}
      {activeDept !== 'home' && activeDept !== 'operations' && activeDept !== 'executive' && activeDept !== 'hr' && activeDept !== 'it' && activeDept !== 'finance' && activeDept !== 'legal' && activeDept !== 'sales' && activeDept !== 'marketing' && activeDept !== 'customer-service' && activeDept !== 'procurement' && activeDept !== 'safety' && activeDept !== 'training' && (
        <DepartmentLandingView
          deptKey={activeDept}
          locked={!canAccessDepartment(user, activeDept)}
          viewerDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null}
        />
      )}
      {activeDept === 'executive' && (
        canAccessDepartment(user, 'executive')
          ? <ExecutiveCommandCenter user={user} boardFilter={deptBoardFilter} onJumpDept={(k) => canAccessDepartment(user, k) ? setActiveDept(k) : toast.error(lockReasonFor(user, k))} />
          : <DepartmentLandingView deptKey="executive" locked viewerDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null} />
      )}
      {activeDept === 'hr' && (
        canAccessDepartment(user, 'hr')
          ? <HRCommandCenter boardFilter={deptBoardFilter} onOpenSection={onOpenDeptSection('hr')} />
          : <DepartmentLandingView deptKey="hr" locked viewerDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null} />
      )}
      {activeDept === 'it' && (
        canAccessDepartment(user, 'it')
          ? <ITCommandCenter boardFilter={deptBoardFilter} />
          : <DepartmentLandingView deptKey="it" locked viewerDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null} />
      )}
      {activeDept === 'finance' && (canAccessDepartment(user, 'finance')
        ? <FinanceCommandCenter boardFilter={deptBoardFilter} onOpenSection={onOpenDeptSection('finance')} />
        : <DepartmentLandingView deptKey="finance" locked viewerDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null} />
      )}
      {activeDept === 'legal' && (canAccessDepartment(user, 'legal')
        ? <LegalCommandCenter boardFilter={deptBoardFilter} onOpenSection={onOpenDeptSection('legal')} />
        : <DepartmentLandingView deptKey="legal" locked viewerDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null} />
      )}
      {activeDept === 'sales' && (canAccessDepartment(user, 'sales')
        ? <SalesCommandCenter boardFilter={deptBoardFilter} onOpenSection={onOpenDeptSection('sales')} />
        : <DepartmentLandingView deptKey="sales" locked viewerDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null} />
      )}
      {activeDept === 'marketing' && (canAccessDepartment(user, 'marketing')
        ? <MarketingCommandCenter boardFilter={deptBoardFilter} onOpenSection={onOpenDeptSection('marketing')} />
        : <DepartmentLandingView deptKey="marketing" locked viewerDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null} />
      )}
      {activeDept === 'customer-service' && (canAccessDepartment(user, 'customer-service')
        ? <CustomerServiceCommandCenter boardFilter={deptBoardFilter} onOpenSection={onOpenDeptSection('customer-service')}
            initialSection={new URLSearchParams(location.search).get('cs_tab')}
            chatSessionId={new URLSearchParams(location.search).get('chat_session')}
            autoClaim={new URLSearchParams(location.search).get('claim') === '1'} />
        : <DepartmentLandingView deptKey="customer-service" locked viewerDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null} />
      )}
      {activeDept === 'procurement' && (canAccessDepartment(user, 'procurement')
        ? <ProcurementCommandCenter boardFilter={deptBoardFilter} onOpenSection={onOpenDeptSection('procurement')} />
        : <DepartmentLandingView deptKey="procurement" locked viewerDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null} />
      )}
      {activeDept === 'safety' && (canAccessDepartment(user, 'safety')
        ? <SafetyCommandCenter boardFilter={deptBoardFilter} onOpenSection={onOpenDeptSection('safety')} />
        : <DepartmentLandingView deptKey="safety" locked viewerDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null} />
      )}
      {activeDept === 'training' && (canAccessDepartment(user, 'training')
        ? <TrainingCommandCenter boardFilter={deptBoardFilter} onOpenSection={onOpenDeptSection('training')} />
        : <DepartmentLandingView deptKey="training" locked viewerDeptCode={(user && user.badge_number) ? user.badge_number.split('-')[2] : null} />
      )}
      {activeDept === 'operations' && (

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb — when we arrived here from a Command Center
            Quick Access tile, offer a fast way back to that dept. */}
        {parentDept && REASSIGNED_SECTIONS.includes(activeTab) && (
          <div className="mb-6 flex items-center gap-3 flex-wrap" data-testid={`section-breadcrumb-${activeTab}`} data-parent-readonly={parentReadOnly ? 'true' : 'false'}>
            <button
              type="button"
              onClick={() => { setActiveDept(parentDept); setActiveTab('home'); setParentDept(null); setParentReadOnly(false); }}
              className="text-xs uppercase tracking-[0.25em] font-semibold text-[#C9A962] hover:text-white transition-colors flex items-center gap-2"
              data-testid={`section-breadcrumb-back-${parentDept}`}
            >
              <span className="text-lg leading-none">&larr;</span>
              Back to {(parentDept || '').replace('-', ' ')} Command Center
            </button>
            <span className="text-zinc-600">·</span>
            <span className="text-xs uppercase tracking-[0.25em] text-zinc-400">{activeTab.replace('-', ' ')}</span>
            {parentReadOnly && (
              <span
                className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded"
                style={{ backgroundColor: '#78350f', color: '#fbbf24', border: '1px solid #b45309' }}
                data-testid="section-readonly-banner"
                title="You opened this section from a department that has read-only access. Editing actions are hidden."
              >
                View only
              </span>
            )}
          </div>
        )}

        {parentDept && REASSIGNED_SECTIONS.includes(activeTab) && <UnifiedGeoRoleFilter filter={deptBoardFilter} testIdPrefix="unified-filter-section" />}
        {/* Operations header + stats cards — hidden when we're rendering
            a section that belongs to another department. */}
        {!(parentDept && REASSIGNED_SECTIONS.includes(activeTab)) && (
          <>
            <OperationsHeader />

        {/* Iter-111 · Fix 1 — Enterprise Pivot Filter mounted below the
            Operations header, last element before the board data. */}
        <UnifiedGeoRoleFilter filter={deptBoardFilter} testIdPrefix="unified-filter-operations" />

        {/* Iter-144 · FIX 2 — When the Role selector is set to Cleaners
            on the Operations Enterprise Pivot Board, mount the full
            Commercial Cleaners HQ inline (same 9-line + boards layout
            already used inside the /staff-app mobile PWA and the
            standalone /cleaners-command-center admin page). The board
            reads exclusively from /api/cleaners/* endpoints. It reuses
            the same filter above, so cascade / snapshot / preset /
            state / city choices all propagate live to the Cleaners
            data. */}

        {/* Iter-105 · Item 1 — View-as toggle is rendered above by
            `DepartmentBoardFilter`. We keep the "View as Field Staff"
            escape hatch here since it's an admin-only navigation. */}
        <div className="flex items-center justify-end mb-4 gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => navigate('/staff-dashboard?view=staff')}
            className="text-xs font-semibold px-3 py-1.5 rounded border transition"
            style={{ color: '#C9A962', borderColor: 'rgba(201,169,98,0.4)' }}
            data-testid="view-as-staff-btn"
          >
            View as {scopeTerms.staff} →
          </button>
        </div>

        {/* Iter-104 · Item 1 — Cleaners view NO LONGER shows a demo-mode
            banner. Cleaners CC is live, wired to /api/cleaners/*. When
            a section has no live data, it renders as an empty live
            state (zero counts), never a demo placeholder. */}

        {/* Iter-85 · CEO Strategy scorecards moved to Executive Command
            Center. The Operations dept no longer duplicates them so the
            landing here stays focused on daily field ops. */}

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="operations-department-metrics">
          <Card className="bg-zinc-900 border-l-4 border-l-red-500 p-6" data-testid="operations-kpi-pending">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                PENDING REVIEW
              </h3>
              <AlertTriangle className="h-6 w-6 text-red-500" />
            </div>
            <p className="text-3xl font-black text-white">{pendingViolations.length}</p>
            <p className="text-sm text-zinc-400">{scopeTerms.staff} reports awaiting review</p>
          </Card>

          <Card className="bg-zinc-900 border-l-4 border-l-amber-500 p-6" data-testid="operations-kpi-occupant-reports">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                {scopeTerms.occupant.toUpperCase()} REPORTS
              </h3>
              <FileText className="h-6 w-6 text-amber-500" />
            </div>
            <p className="text-3xl font-black text-white">
              {allViolations.filter(v => v.source === 'tenant').length}
            </p>
            <p className="text-sm text-zinc-400">Direct {scopeTerms.occupant.toLowerCase()} complaints</p>
          </Card>

          <Card className="bg-zinc-900 border-l-4 border-l-green-500 p-6" data-testid="operations-kpi-active-staff">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                ACTIVE {scopeTerms.staff.toUpperCase()}
              </h3>
              <Users className="h-6 w-6 text-green-500" />
            </div>
            <p className="text-3xl font-black text-white">{activeEmployees.filter(e => !['admin', 'company_manager'].includes(e.role)).length}</p>
            <p className="text-sm text-zinc-400">{blockedEmployees.length} blocked</p>
          </Card>

          <Card className="bg-zinc-900 border-l-4 border-l-[#FFD60A] p-6" data-testid="operations-kpi-on-duty">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                TODAY'S OPS
              </h3>
              <Calendar className="h-6 w-6 text-[#FFD60A]" />
            </div>
            <p className="text-3xl font-black text-white">
              {dailySummary?.summary?.employees_working || 0}
            </p>
            <p className="text-sm text-zinc-400">{scopeTerms.staff} on duty</p>
          </Card>
        </div>

        {/* Section 8 (iter-137) — New Account Onboarding relocated to the
            Customer Service command center. Ops CC no longer surfaces the
            checklist panel; the underlying data + endpoints remain. */}
          </>
        )}

        {/* Main Tabs */}
        <Tabs key={`${deptBoardFilter.queryString}-${deptBoardFilter.refreshTick}`} value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          {!(parentDept && REASSIGNED_SECTIONS.includes(activeTab)) && (
          <OperationsMenuTabs value={activeTab} onChange={setActiveTab} staffLabel={scopeTerms.staff} counts={{ violations: pendingViolations.length, swaps: swapRequests.filter(s => s.status === 'pending_manager').length, fleet: safetyReports.length, reports: dailyReports.length }} />
          )}

          {/* Statistics — enterprise pivot across state / region / city × service.
              Billing snapshot is hidden here (Ops view) — Finance sees it via
              their Command Center quick-access tile which routes to the same tab
              and re-mounts with hideBilling=false. */}
          <TabsContent value="statistics" className="space-y-4">
            <AdminStatisticsDashboard
              regions={regions}
              properties={properties}
              hideBilling={parentDept !== 'finance'}
            />
          </TabsContent>

          {/* Emergency Ops — Admin-only SMS blackout broadcasts + audit trail.
              Now wrapped with the same Big-city / State scope chrome as the
              Violations and Blackouts tabs for visual + operational parity. */}
          <TabsContent value="emergency-ops" className="space-y-4" data-testid="admin-emergency-ops-content">
            <ManualOutageControl user={user} properties={properties} />
            <MetroKindHeader
              bigCity={companyMetro}
              onBigCity={setCompanyMetro}
              testid="emergency-ops"
            />
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={[]}
              sectionLabel="Broadcast Target"
              sectionIcon={AlertTriangle}
              renderItems={() => (
                <p className="text-[11px] text-zinc-500 italic px-2 py-1">
                  Pick a region / city / property to pre-scope the emergency broadcast below.
                </p>
              )}
              emptyItemsLabel="Select a region, city, or property to focus the broadcast."
              testIdPrefix="drilldown-emergency-ops"
            />
            <EmergencyBroadcastPanel user={user} regions={regions} />
          </TabsContent>

          {/* Violations Review Tab */}
          {/* Admin Home — overview + deep-link cards */}
          {/* Regional Command tab removed (iter-78 part 4) — that view now
              lives exclusively on the Regional Manager dashboard
              (StaffDashboard Team Assignments tab). The CEO's strategic
              landing is Executive HQ (`value="home"`). */}

          <TabsContent value="home" className="space-y-4">
            <AdminHome
              onNavigate={(t, subtype) => {
                if (subtype) setSpecialtyServiceType(subtype);
                setActiveTab(t);
              }}
              onFilterTo={({ tab }) => {
                // Deep-link a Recent Activity row into its native tab.
                // The Region → City → Property drilldown at the top of
                // the destination tab picks up on the property in-scope
                // via the property_id filter (future enhancement).
                if (tab) setActiveTab(tab);
              }}
            />
          </TabsContent>

          {/* Ops Staffing Roster — read-only staffing view. Employee
              CRUD lives in HR → Employees (Command Center · Access
              Areas). Here Ops managers just see who's on the field. */}
          <TabsContent value="ops-staffing" className="space-y-4">
            <CrewLeaderboard />
            <BonusPanel />
            <OpsStaffingRoster employees={employees} />
          </TabsContent>

          {/* Ops Properties view — read-only listing of properties +
              their service agreements. Pricing / contract edits happen
              in HR, Finance, Legal, and Sales command centers. */}
          <TabsContent value="ops-properties" className="space-y-4">
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={contracts}
              sectionLabel="Properties · Agreements"
              sectionIcon={FileText}
              renderItems={(items) => (
                <ul className="space-y-1 text-[11px] text-zinc-200">
                  {items.map((c) => (
                    <li key={c.contract_id} className="border border-zinc-800 rounded px-2 py-1 flex justify-between gap-2">
                      <span className="truncate">{c.title || c.original_filename || 'Service Agreement'}</span>
                      <span className="text-zinc-500 flex-shrink-0">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
              emptyItemsLabel="No agreements on file for this property."
              testIdPrefix="drilldown-properties"
            />
            <OpsPropertiesView properties={properties} contracts={contracts} viewAs={deptBoardFilter.view_as} />
          </TabsContent>

          {/* Ops Training Completions — read-only certifications list.
              Module authoring lives in Safety / Marketing / Sales /
              Legal command centers per the ownership matrix. */}
          <TabsContent value="ops-training-completions" className="space-y-4">
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={[]}
              sectionLabel="Training Completions"
              sectionIcon={GraduationCap}
              emptyItemsLabel="Training completions aren't property-scoped — the roster below is filtered by agent."
              testIdPrefix="drilldown-training"
            />
            <OpsTrainingCompletions />
          </TabsContent>

          <TabsContent value="violations" className="space-y-4">
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={allViolations}
              sectionLabel="Violations"
              sectionIcon={AlertTriangle}
              renderItems={(items) => (
                <ul className="space-y-1 text-[11px] text-zinc-200">
                  {items.map((v) => (
                    <li key={v.violation_id || v._id} className="border border-zinc-800 rounded px-2 py-1">
                      <div className="flex justify-between gap-2">
                        <span className="font-bold text-red-400 truncate">{v.violation_type || 'Violation'}</span>
                        <span className="text-zinc-500 flex-shrink-0 text-[10px]">{v.created_at ? new Date(v.created_at).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="text-zinc-400 truncate">Unit {v.unit_number || '—'} · {v.description || ''}</div>
                    </li>
                  ))}
                </ul>
              )}
              emptyItemsLabel="No violations for this property."
              testIdPrefix="drilldown-violations"
            />
            <Card className="bg-zinc-900 border-2 border-red-500/50 p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                <AlertTriangle className="h-5 w-5 text-red-500 mr-2" />
                PENDING STAFF VIOLATION REPORTS
              </h2>
              <p className="text-sm text-zinc-400 mb-4">
                Review staff-submitted violations before forwarding to Property Managers
              </p>
              
              {pendingViolations.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-zinc-400">No pending violations to review</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {pendingViolations.map((violation) => (
                    <Card 
                      key={violation.violation_id} 
                      className="bg-zinc-800 border-zinc-700 p-4 hover:border-[#FFD60A] transition-all"
                      data-testid={`violation-card-${violation.violation_id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <Badge className="bg-amber-500 text-black">{violation.violation_type}</Badge>
                            <Badge className="bg-zinc-700 text-white">
                              {violation.reporter_badge || 'No Badge'}
                            </Badge>
                          </div>
                          <h3 className="font-bold text-white mb-1">
                            {violation.property_name} - Unit {violation.unit_number}
                          </h3>
                          <p className="text-sm text-zinc-300 mb-2">{violation.description}</p>
                          <div className="flex items-center space-x-4 text-xs text-zinc-500">
                            <span>Reported by: {violation.reporter_name} ({violation.reporter_role})</span>
                            <span>{new Date(violation.created_at).toLocaleString()}</span>
                          </div>
                          
                          {violation.photos && violation.photos.length > 0 && (
                            <div className="flex space-x-2 mt-3">
                              {violation.photos.slice(0, 3).map((photo, idx) => (
                                <img
                                  key={idx}
                                  src={photo.data}
                                  alt={`Evidence ${idx + 1}`}
                                  className="h-16 w-16 object-cover rounded border border-zinc-600 cursor-pointer hover:opacity-80"
                                  onClick={() => window.open(photo.data, '_blank')}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        
                        <div className="flex flex-col space-y-2 ml-4">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button 
                                size="sm" 
                                className="bg-[#FFD60A] text-black hover:bg-[#EAB308]"
                                onClick={() => setShowViolationDetail(violation)}
                                data-testid={`review-violation-${violation.violation_id}`}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                Review
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-zinc-900 text-white border-zinc-800 max-w-2xl">
                              <DialogHeader>
                                <DialogTitle className="text-xl">Review Violation Report</DialogTitle>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="p-3 bg-zinc-800 rounded">
                                    <p className="text-xs text-zinc-400">Property</p>
                                    <p className="font-bold">{violation.property_name}</p>
                                  </div>
                                  <div className="p-3 bg-zinc-800 rounded">
                                    <p className="text-xs text-zinc-400">Unit</p>
                                    <p className="font-bold">{violation.unit_number}</p>
                                  </div>
                                  <div className="p-3 bg-zinc-800 rounded">
                                    <p className="text-xs text-zinc-400">Type</p>
                                    <p className="font-bold">{violation.violation_type}</p>
                                  </div>
                                  <div className="p-3 bg-zinc-800 rounded">
                                    <p className="text-xs text-zinc-400">Reporter</p>
                                    <p className="font-bold">{violation.reporter_name}</p>
                                    <p className="text-xs text-zinc-400">{violation.reporter_badge}</p>
                                  </div>
                                </div>
                                
                                <div className="p-3 bg-zinc-800 rounded">
                                  <p className="text-xs text-zinc-400">Description</p>
                                  <p>{violation.description}</p>
                                </div>
                                
                                {violation.photos && violation.photos.length > 0 && (
                                  <div>
                                    <p className="text-xs text-zinc-400 mb-2">Photo Evidence</p>
                                    <div className="flex space-x-2">
                                      {violation.photos.map((photo, idx) => (
                                        <img
                                          key={idx}
                                          src={photo.data}
                                          alt={`Evidence ${idx + 1}`}
                                          className="h-24 w-24 object-cover rounded cursor-pointer"
                                          onClick={() => window.open(photo.data, '_blank')}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                <div>
                                  <Label className="text-white">Review Notes (Optional)</Label>
                                  <Textarea
                                    value={forwardNotes}
                                    onChange={(e) => setForwardNotes(e.target.value)}
                                    placeholder="Add notes for the Property Manager..."
                                    className="bg-zinc-800 border-zinc-700 text-white"
                                  />
                                </div>
                                
                                <div>
                                  <Label className="text-white">Rejection Reason (if rejecting)</Label>
                                  <Input
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    placeholder="Insufficient evidence..."
                                    className="bg-zinc-800 border-zinc-700 text-white"
                                  />
                                </div>
                              </div>
                              <DialogFooter className="flex space-x-3">
                                <Button
                                  variant="destructive"
                                  onClick={() => handleRejectViolation(violation.violation_id)}
                                  data-testid="reject-violation-btn"
                                >
                                  <X className="h-4 w-4 mr-1" />
                                  Reject
                                </Button>
                                <Button
                                  className="bg-[#FFD60A] text-black hover:bg-[#EAB308]"
                                  onClick={() => handleForwardViolation(violation.violation_id)}
                                  data-testid="forward-violation-btn"
                                >
                                  <Send className="h-4 w-4 mr-1" />
                                  Forward to Property Manager
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>

            {/* Tenant Reports Section */}
            <Card className="bg-zinc-900 border-2 border-amber-500/50 p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                <FileText className="h-5 w-5 text-amber-500 mr-2" />
                RECENT {scopeTerms.occupant.toUpperCase()} REPORTS
              </h2>
              <p className="text-sm text-zinc-400 mb-4">
                {scopeTerms.occupant} complaints are automatically sent to both you and the Property Manager
              </p>
              
              {allViolations.filter(v => v.source === 'tenant').length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-zinc-400">No {scopeTerms.occupant.toLowerCase()} reports</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {allViolations.filter(v => v.source === 'tenant').slice(0, 5).map((violation) => (
                    <Card key={violation.violation_id} className="bg-zinc-800 border-zinc-700 p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center space-x-2 mb-1">
                            <Badge className="bg-amber-500 text-black">{violation.violation_type}</Badge>
                            <Badge className={violation.status === 'resolved' ? 'bg-green-500' : 'bg-blue-500'}>
                              {violation.status}
                            </Badge>
                          </div>
                          <p className="font-bold text-white">
                            {violation.property_name} - Unit {violation.unit_number}
                          </p>
                          <p className="text-sm text-zinc-400">
                            By: {violation.reporter_name} • {new Date(violation.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Employee Management Tab */}
          <TabsContent value="employees" className="space-y-4 min-w-0 [overflow-wrap:anywhere]">
            <div className="flex flex-wrap gap-3 justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                EMPLOYEE MANAGEMENT
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <BulkImportEmployeesDialog onCompleted={() => { fetchEmployees(); fetchStaffGallery(); }} />
                <AddEmployeeDialog onCreated={() => { fetchEmployees(); fetchStaffGallery(); }} />
              </div>
            </div>

            {/* ── Big City tab strip — scopes the roster below to the
                selected metro (Dallas · Houston · Austin · San Antonio ·
                Tyler). Matches the switcher used everywhere else. */}
            <div
              className="mb-4 flex items-center justify-between gap-3 flex-wrap p-3 rounded-lg bg-zinc-900 border-2 border-[#C9A962]/40"
              data-testid="employees-bigcity-tabs"
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[9px] uppercase tracking-widest text-[#C9A962] font-bold mr-2">
                    Big City
                  </span>
                  {['Dallas', 'Houston', 'Austin', 'San Antonio', 'Tyler'].map((c) => {
                    const active = empBigCity === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setEmpBigCity(c)}
                        className={`px-3.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest transition-all border ${
                          active
                            ? 'bg-[#FFD60A] text-black border-[#FFD60A] shadow-[0_0_0_1px_#FFD60A]'
                            : 'bg-zinc-950 text-zinc-300 border-zinc-700 hover:border-[#C9A962] hover:text-[#C9A962]'
                        }`}
                        data-testid={`employees-bigcity-tab-${c.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
                {/* Admin / Staff kind toggle — hides one population
                    entirely when picked. Matches the Attendance page
                    pattern. */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[9px] uppercase tracking-widest text-zinc-400 font-bold mr-2">
                    Filter
                  </span>
                  {[
                    { k: 'all',   label: 'All' },
                    { k: 'admin', label: 'Admin (Corporate)' },
                    { k: 'staff', label: scopeTerms.staff },
                  ].map(({ k, label }) => {
                    const active = empKind === k;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setEmpKind(k)}
                        className={`px-3 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest transition-all border ${
                          active
                            ? 'bg-cyan-500 text-black border-cyan-500'
                            : 'bg-zinc-900 text-zinc-300 border-zinc-700 hover:border-cyan-500 hover:text-cyan-400'
                        }`}
                        data-testid={`employees-kind-${k}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-zinc-300">
                <Badge className="bg-green-700 text-white" data-testid="employees-active-count">
                  {activeEmployees.length} active
                </Badge>
                <Badge className="bg-red-800 text-red-100" data-testid="employees-blocked-count">
                  {blockedEmployees.length} blocked
                </Badge>
                <Badge className="bg-zinc-700 text-zinc-300" data-testid="employees-terminated-count">
                  {terminatedEmployees.length} terminated
                </Badge>
                {(() => {
                  // Corporate ↔ Field ratio gauge — health signal on
                  // the active roster only (terminated + blocked
                  // employees aren't billable).
                  const { admin, staff } = splitAdminStaff(activeEmployees);
                  const target = 25; // 1 admin per 25 field agents
                  const ratio = staff.length === 0
                    ? (admin.length === 0 ? '—' : `${admin.length} : 0`)
                    : `1 : ${(staff.length / Math.max(admin.length, 1)).toFixed(0)}`;
                  const healthy = admin.length > 0 && (staff.length / admin.length) <= target * 1.2;
                  return (
                    <span
                      className={`ml-2 flex items-center gap-1.5 px-2.5 py-1 rounded font-mono text-[11px] border ${
                        healthy
                          ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-300'
                          : 'bg-amber-950/40 border-amber-500/40 text-amber-300'
                      }`}
                      title={`Corporate ↔ Field ratio for ${empBigCity} (target: 1 admin per ${target} field agents)`}
                      data-testid="employees-ratio-gauge"
                    >
                      <span className="text-[9px] uppercase tracking-widest opacity-70">Corp↔Field</span>
                      <span className="font-bold">{ratio}</span>
                      <span className="text-[9px] opacity-60">· {admin.length}A / {staff.length}F</span>
                    </span>
                  );
                })()}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 min-w-0">
              {/* Active Employees */}
              <Card className="bg-zinc-900 border-2 border-green-500/50 p-4 sm:p-6 min-w-0">
                <h3 className="font-bold text-lg mb-4 flex items-center text-white">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  Active Employees ({activeEmployees.length})
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="active-employees-list">
                  {(() => {
                    const { admin, staff } = splitAdminStaff(activeEmployees);
                    const renderRow = (employee) => (
                    <Card key={employee.user_id} className="bg-zinc-800 border-zinc-700 p-3 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 min-w-0">
                        <div className="flex items-center space-x-3 min-w-0 flex-1">
                          <div className="h-10 w-10 rounded-full bg-[#FFD60A] flex items-center justify-center overflow-hidden">
                            {employee.profile_photo ? (
                              <img src={employee.profile_photo} alt={employee.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-black font-bold text-sm">
                                {getInitials(employee.name)}
                              </span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-white break-words">{employee.name}</p>
                            <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                              <Badge className="bg-zinc-700 text-xs max-w-full whitespace-normal break-all">{employee.badge_number}</Badge>
                              <Badge className={
                                employee.role === 'supervisor' ? 'bg-purple-500' :
                                employee.role === 'crew_leader' ? 'bg-blue-500' : 'bg-zinc-600'
                              }>
                                {employee.role.replace('_', ' ')}
                              </Badge>
                              {employee.assigned_shift && (
                                <Badge className="bg-zinc-600 text-xs whitespace-normal" data-testid={`employee-schedule-${employee.user_id}`}>
                                  {employeeScheduleLabel(employee)}
                                </Badge>
                              )}
                              {employee.assigned_vehicle_label && (
                                <Badge
                                  className="bg-teal-700 text-white text-[10px] font-bold"
                                  title="Assigned company vehicle"
                                  data-testid={`employee-vehicle-${employee.user_id}`}
                                >
                                  🚐 {employee.assigned_vehicle_label}
                                  {employee.assigned_vehicle_plate ? ` · ${employee.assigned_vehicle_plate}` : ''}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 self-end sm:self-auto shrink-0">
                          <Button
                            size="sm"
                            className="bg-[#C9A962] hover:bg-[#b89651] text-black font-bold"
                            onClick={() => handleImpersonateEmployee(employee)}
                            data-testid={`impersonate-employee-${employee.user_id}`}
                            title="Step into this employee's shoes"
                          >
                            <UserCog className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-400 border-red-400 hover:bg-red-900/50"
                            onClick={() => handleToggleBlock(employee)}
                            data-testid={`block-employee-${employee.user_id}`}
                            title="Block temporarily"
                          >
                            <Ban className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-zinc-400 border-zinc-500 hover:bg-zinc-800"
                            onClick={() => handleToggleTerminate(employee)}
                            data-testid={`terminate-employee-${employee.user_id}`}
                            title="Terminate — mark as ex-employee"
                          >
                            <UserX className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                    );
                    return (
                      <>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[#C9A962] pt-1" data-testid="active-admin-header">
                          <span>Admin (Corporate)</span>
                          <span className="flex-1 h-px bg-[#C9A962]/30" />
                          <span className="font-mono">{admin.length}</span>
                        </div>
                        {admin.length === 0
                          ? <p className="text-zinc-500 text-[11px] italic text-center py-1">No corporate admins in {empBigCity}.</p>
                          : admin.map(renderRow)}
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-green-400 pt-3" data-testid="active-staff-header">
                          <span>{scopeTerms.staff}</span>
                          <span className="flex-1 h-px bg-green-500/30" />
                          <span className="font-mono">{staff.length}</span>
                        </div>
                        {staff.length === 0
                          ? <p className="text-zinc-500 text-[11px] italic text-center py-1">No field agents in {empBigCity}.</p>
                          : staff.map(renderRow)}
                      </>
                    );
                  })()}
                </div>
              </Card>

              {/* Blocked Employees */}
              <Card className="bg-zinc-900 border-2 border-red-500/50 p-4 sm:p-6 min-w-0">
                <h3 className="font-bold text-lg mb-4 flex items-center text-white">
                  <UserX className="h-5 w-5 text-red-500 mr-2" />
                  Blocked Employees ({blockedEmployees.length})
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="blocked-employees-list">
                  {blockedEmployees.length === 0 ? (
                    <p className="text-zinc-400 text-center py-4">No blocked employees</p>
                  ) : (() => {
                    const { admin, staff } = splitAdminStaff(blockedEmployees);
                    const renderRow = (employee) => (
                      <Card key={employee.user_id} className="bg-zinc-800 border-red-900 p-3 opacity-75 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 min-w-0">
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <div className="h-10 w-10 rounded-full bg-red-900 flex items-center justify-center">
                              <span className="text-red-400 font-bold text-sm">
                                {employee.name?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-white break-words">{employee.name}</p>
                              <div className="flex flex-wrap gap-2 items-center">
                                <Badge className="bg-zinc-700 text-xs max-w-full whitespace-normal break-all">{employee.badge_number}</Badge>
                                <Badge className="bg-red-900 text-red-400">BLOCKED</Badge>
                              </div>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleToggleBlock(employee)}
                            data-testid={`unblock-employee-${employee.user_id}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    );
                    return (
                      <>
                        {admin.length > 0 && (
                          <>
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[#C9A962] pt-1" data-testid="blocked-admin-header">
                              <span>Admin (Corporate)</span>
                              <span className="flex-1 h-px bg-[#C9A962]/30" />
                              <span className="font-mono">{admin.length}</span>
                            </div>
                            {admin.map(renderRow)}
                          </>
                        )}
                        {staff.length > 0 && (
                          <>
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-red-400 pt-3" data-testid="blocked-staff-header">
                              <span>{scopeTerms.staff}</span>
                              <span className="flex-1 h-px bg-red-500/30" />
                              <span className="font-mono">{staff.length}</span>
                            </div>
                            {staff.map(renderRow)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </Card>

              {/* Inactive / Terminated Employees (Feb 2026 CEO directive) */}
              <Card className="bg-zinc-900 border-2 border-zinc-600/60 p-6" data-testid="terminated-employees-card">
                <h3 className="font-bold text-lg mb-4 flex items-center text-white">
                  <UserX className="h-5 w-5 text-zinc-400 mr-2" />
                  Inactive / Terminated ({terminatedEmployees.length})
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="terminated-employees-list">
                  {terminatedEmployees.length === 0 ? (
                    <p className="text-zinc-500 text-center py-4 text-sm italic">
                      No inactive or terminated employees in {empBigCity}.
                    </p>
                  ) : (() => {
                    const { admin, staff } = splitAdminStaff(terminatedEmployees);
                    const renderRow = (employee) => {
                      const rehire = rehireEligible(employee);
                      return (
                      <Card key={employee.user_id} className="bg-zinc-800 border-zinc-700 p-3 opacity-70 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 min-w-0">
                          <div className="flex items-center space-x-3 min-w-0 flex-1">
                            <div className="h-10 w-10 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden">
                              {employee.profile_photo ? (
                                <img src={employee.profile_photo} alt={employee.name} className="w-full h-full object-cover grayscale" />
                              ) : (
                                <span className="text-zinc-300 font-bold text-sm">
                                  {getInitials(employee.name)}
                                </span>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="font-bold text-zinc-300 line-through decoration-zinc-600">
                                {employee.name}
                              </p>
                              <div className="flex items-center space-x-2 flex-wrap">
                                <Badge className="bg-zinc-700 text-xs">{employee.badge_number}</Badge>
                                <Badge className="bg-zinc-600 text-zinc-100 text-[10px]">TERMINATED</Badge>
                                {rehire && (
                                  <Badge
                                    className="bg-emerald-700 text-white text-[10px] font-bold"
                                    title="Terminated 180+ days ago for a non-misconduct reason — recruiter can fast-track rehire"
                                    data-testid={`rehire-eligible-${employee.user_id}`}
                                  >
                                    REHIRE-ELIGIBLE
                                  </Badge>
                                )}
                                {employee.termination_reason && (
                                  <span className="text-[10px] text-zinc-500 italic truncate max-w-[180px]" title={employee.termination_reason}>
                                    {employee.termination_reason}
                                  </span>
                                )}
                              </div>
                              {employee.terminated_at && (
                                <p className="text-[10px] text-zinc-500 mt-0.5">
                                  Terminated {new Date(employee.terminated_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-400 border-emerald-400 hover:bg-emerald-900/40"
                            onClick={() => handleToggleTerminate(employee)}
                            data-testid={`reinstate-employee-${employee.user_id}`}
                            title="Reinstate to Active roster"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                      );
                    };
                    return (
                      <>
                        {admin.length > 0 && (
                          <>
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[#C9A962] pt-1" data-testid="terminated-admin-header">
                              <span>Admin (Corporate)</span>
                              <span className="flex-1 h-px bg-[#C9A962]/30" />
                              <span className="font-mono">{admin.length}</span>
                            </div>
                            {admin.map(renderRow)}
                          </>
                        )}
                        {staff.length > 0 && (
                          <>
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-zinc-400 pt-3" data-testid="terminated-staff-header">
                              <span>{scopeTerms.staff}</span>
                              <span className="flex-1 h-px bg-zinc-500/30" />
                              <span className="font-mono">{staff.length}</span>
                            </div>
                            {staff.map(renderRow)}
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </Card>
            </div>

            {/* Staff Performance — four admin-managed factors feed the
                compliance trophy on staff dashboards */}
            <StaffPerformancePanel />
          </TabsContent>

          {/* All Reports Tab */}
          <TabsContent value="reports" className="space-y-4">
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={dailyReports}
              getItemPropertyId={(r) => r.property_id}
              sectionLabel="All Reports"
              sectionIcon={FileText}
              renderItems={(items) => (
                <ul className="space-y-1 text-[11px] text-zinc-200">
                  {items.map((r) => (
                    <li key={r.report_id || r._id} className="border border-zinc-800 rounded px-2 py-1">
                      <div className="flex justify-between gap-2">
                        <span className="font-bold text-white truncate">{r.date || r.type || 'Report'}</span>
                        <span className="text-zinc-500 flex-shrink-0 text-[10px]">{r.units_serviced ?? ''}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              emptyItemsLabel="No reports of any kind for this property."
              testIdPrefix="drilldown-all-reports"
            />
            <AdminViolationReportsTab allViolations={allViolations} />
          </TabsContent>

          {/* Schedules Tab */}
          <TabsContent value="schedules" className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center space-x-4">
                <h2 className="text-xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                  STAFF SCHEDULES
                </h2>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setTimeout(fetchSchedules, 100);
                  }}
                  className="bg-zinc-800 border-zinc-700 text-white w-40"
                />
              </div>
              <Dialog open={showAddSchedule} onOpenChange={setShowAddSchedule}>
                <DialogTrigger asChild>
                  <Button className="bg-[#FFD60A] text-black hover:bg-[#EAB308]" data-testid="create-schedule-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Schedule
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-zinc-900 text-white border-zinc-800">
                  <DialogHeader>
                    <DialogTitle>Create Staff Schedule</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateSchedule} className="space-y-4">
                    <div>
                      <Label className="text-white">Employee</Label>
                      <Select 
                        value={scheduleForm.user_id} 
                        onValueChange={(value) => setScheduleForm({...scheduleForm, user_id: value})}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                          <SelectValue placeholder="Select employee" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {activeEmployees.map((emp) => (
                            <SelectItem key={emp.user_id} value={emp.user_id}>
                              {emp.name} ({emp.badge_number}) - {emp.role}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white">Date</Label>
                      <Input
                        type="date"
                        value={scheduleForm.date}
                        onChange={(e) => setScheduleForm({...scheduleForm, date: e.target.value})}
                        required
                        className="bg-zinc-800 border-zinc-700 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white">Shift</Label>
                      <Select 
                        value={scheduleForm.shift_type} 
                        onValueChange={(value) => setScheduleForm({...scheduleForm, shift_type: value})}
                      >
                        <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {STAFF_SHIFT_ORDER.map((code) => (
                            <SelectItem key={code} value={code}>
                              {shiftLabel(code)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="bg-zinc-800 p-3 rounded text-sm">
                      <p className="text-zinc-400">
                        <Clock className="h-4 w-4 inline mr-1" />
                        Grace period: 15 minutes after shift start
                      </p>
                      <p className="text-zinc-400 mt-1">
                        <AlertTriangle className="h-4 w-4 inline mr-1" />
                        Late notification required 3+ hours before shift
                      </p>
                    </div>
                    <Button type="submit" className="w-full bg-[#FFD60A] text-black hover:bg-[#EAB308] font-bold">
                      Create Schedule
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Staff Gallery */}
            <Card className="bg-zinc-900 border-2 border-[#FFD60A]/50 p-6 mb-4">
              <h3 className="font-bold text-lg mb-4 flex items-center text-white">
                <Users className="h-5 w-5 text-[#FFD60A] mr-2" />
                STAFF GALLERY
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4">
                {staffGallery.map((staff) => (
                  <div key={staff.user_id} className="text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden border-2 border-zinc-700">
                      {staff.profile_photo ? (
                        <img src={staff.profile_photo} alt={staff.name} className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-8 w-8 text-zinc-500" />
                      )}
                    </div>
                    <p className="text-xs text-white mt-1 truncate">{staff.name}</p>
                    <Badge className={`text-xs ${
                      staff.role === 'supervisor' ? 'bg-purple-500' :
                      staff.role === 'crew_leader' ? 'bg-blue-500' : 'bg-zinc-600'
                    }`}>
                      {staff.badge_number}
                    </Badge>
                  </div>
                ))}
              </div>
            </Card>

            {/* Schedule Grid */}
            <Card className="bg-zinc-900 p-6">
              <h3 className="font-bold text-lg mb-4 text-white">
                Schedules for {new Date(selectedDate).toLocaleDateString()}
              </h3>
              {schedules.length === 0 ? (
                <p className="text-zinc-400 text-center py-8">No schedules for this date</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-4">
                  {schedules.map((schedule) => (
                    <Card key={schedule.schedule_id} className={`p-4 ${
                      schedule.status === 'completed' ? 'bg-green-900/30 border-green-600' :
                      schedule.is_late ? 'bg-red-900/30 border-red-600' :
                      'bg-zinc-800 border-zinc-700'
                    }`}>
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden">
                          {schedule.profile_photo ? (
                            <img src={schedule.profile_photo} alt={schedule.user_name} className="w-full h-full object-cover" />
                          ) : (
                            <User className="h-6 w-6 text-zinc-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-white">{schedule.user_name}</p>
                          <div className="flex items-center space-x-2">
                            <Badge className="bg-zinc-700 text-xs">{schedule.badge_number}</Badge>
                            <Badge className={schedule.shift_type === 'shift_1' ? 'bg-blue-500' : 'bg-purple-500'}>
                              {SHIFTS[schedule.shift_type]?.name}
                            </Badge>
                          </div>
                          <p className="text-xs text-zinc-400 mt-1">
                            {schedule.start_time} - {schedule.end_time}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge className={
                            schedule.status === 'completed' ? 'bg-green-500' :
                            schedule.status === 'clocked_in' ? 'bg-blue-500' :
                            schedule.is_late ? 'bg-red-500' : 'bg-zinc-600'
                          }>
                            {schedule.is_late ? 'LATE' : schedule.status}
                          </Badge>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Swap Requests Tab */}
          <TabsContent value="swaps" className="space-y-4">
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={[]}
              sectionLabel="Shift Swaps"
              sectionIcon={ArrowRightLeft}
              emptyItemsLabel="Swap requests are agent-scoped, not property-scoped — full queue is below."
              testIdPrefix="drilldown-swaps"
            />
            <Card className="bg-zinc-900 border-2 border-amber-500/50 p-6">
              <h2 className="text-xl font-bold mb-4 flex items-center text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                <ArrowRightLeft className="h-5 w-5 text-amber-500 mr-2" />
                PENDING SWAP APPROVALS
              </h2>
              <p className="text-sm text-zinc-400 mb-4">
                Final approval for shift swaps (already approved by Supervisor)
              </p>
              
              {swapRequests.filter(s => s.status === 'pending_manager').length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-zinc-400">No pending swap requests</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {swapRequests.filter(s => s.status === 'pending_manager').map((swap) => (
                    <Card key={swap.swap_id} className="bg-zinc-800 border-zinc-700 p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-white">
                            <span className="font-bold">{swap.requester_name}</span>
                            <ArrowRightLeft className="h-4 w-4 inline mx-2" />
                            <span className="font-bold">{swap.target_user_name}</span>
                          </p>
                          <p className="text-sm text-zinc-400">
                            Date: {swap.date} • {SHIFTS[swap.shift_type]?.name}
                          </p>
                          {swap.reason && <p className="text-xs text-zinc-500 mt-1">Reason: {swap.reason}</p>}
                          <Badge className="bg-green-600 mt-2">Supervisor Approved</Badge>
                        </div>
                        <div className="flex space-x-2">
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleRejectSwap(swap.swap_id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            className="bg-[#FFD60A] text-black hover:bg-[#EAB308]"
                            onClick={() => handleApproveSwap(swap.swap_id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>

            {/* All Swap History */}
            <Card className="bg-zinc-900 p-6">
              <h3 className="font-bold text-lg mb-4 text-white">Swap History</h3>
              <div className="space-y-3">
                {swapRequests.filter(s => s.status !== 'pending_manager').map((swap) => (
                  <Card key={swap.swap_id} className="bg-zinc-800 border-zinc-700 p-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-white">
                          {swap.requester_name} ↔ {swap.target_user_name}
                        </p>
                        <p className="text-xs text-zinc-400">{swap.date}</p>
                      </div>
                      <Badge className={
                        swap.status === 'approved' ? 'bg-green-500' :
                        swap.status === 'rejected' ? 'bg-red-500' : 'bg-amber-500'
                      }>
                        {swap.status}
                      </Badge>
                    </div>
                  </Card>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Pricing Requests Tab */}
          <TabsContent value="pricing" className="space-y-4">
            <MetroKindHeader
              bigCity={companyMetro}
              onBigCity={setCompanyMetro}
              testid="pricing"
            />
            <Card className="bg-zinc-900 border-2 border-[#FFD60A]/40 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                    PRICING REQUESTS
                  </h2>
                  <p className="text-sm text-zinc-400">Respond to property manager pricing requests</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="bg-[#FFD60A] text-black" data-testid="pricing-request-count">
                    {pendingPricingRequests.length} Pending
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#FFD60A] text-[#FFD60A]"
                    onClick={exportPricingCSV}
                    data-testid="pricing-export-csv"
                  >
                    Export CSV
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-[#FFD60A] text-[#FFD60A]"
                    onClick={exportPricingPDF}
                    data-testid="pricing-export-pdf"
                  >
                    Export PDF
                  </Button>
                </div>
              </div>

              <div className="flex flex-wrap items-end gap-4 mb-6" data-testid="pricing-filters">
                <div className="min-w-[160px]">
                  <Label htmlFor="pricing-filter-status">Status</Label>
                  <Select
                    value={pricingFilters.status}
                    onValueChange={(value) => setPricingFilters({ ...pricingFilters, status: value })}
                  >
                    <SelectTrigger id="pricing-filter-status" data-testid="pricing-filter-status">
                      <SelectValue placeholder="All statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="quoted">Quoted</SelectItem>
                      <SelectItem value="needs_info">Needs Info</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="pricing-filter-start">From</Label>
                  <Input
                    id="pricing-filter-start"
                    type="date"
                    value={pricingFilters.startDate}
                    onChange={(e) => setPricingFilters({ ...pricingFilters, startDate: e.target.value })}
                    data-testid="pricing-filter-start-date"
                  />
                </div>
                <div>
                  <Label htmlFor="pricing-filter-end">To</Label>
                  <Input
                    id="pricing-filter-end"
                    type="date"
                    value={pricingFilters.endDate}
                    onChange={(e) => setPricingFilters({ ...pricingFilters, endDate: e.target.value })}
                    data-testid="pricing-filter-end-date"
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  className="border-zinc-600 text-zinc-300"
                  onClick={() => setPricingFilters({ status: 'all', startDate: '', endDate: '' })}
                  data-testid="pricing-filter-reset"
                >
                  Reset Filters
                </Button>
                <p className="text-xs text-zinc-400" data-testid="pricing-filter-count">
                  Showing {filteredPricingRequests.length} of {pricingRequests.length}
                </p>
              </div>

              {filteredPricingRequests.length === 0 ? (
                <div className="text-center py-10" data-testid="pricing-empty-state">
                  <DollarSign className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400">No pricing requests yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredPricingRequests.map((request) => (
                    <Card
                      key={request.request_id}
                      className="bg-zinc-800 border-zinc-700 p-4"
                      data-testid={`pricing-request-card-${request.request_id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        <div>
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <Badge className="bg-zinc-700 text-white">{request.property_name || 'Property'}</Badge>
                            <Badge className="bg-[#FFD60A] text-black">{request.package_type || 'Package TBD'}</Badge>
                            <Badge className={request.status === 'submitted' ? 'bg-red-500 text-white' : 'bg-green-600 text-white'}>
                              {request.status?.toUpperCase()}
                            </Badge>
                          </div>
                          <p className="text-white font-semibold" data-testid={`pricing-contact-${request.request_id}`}>
                            {request.contact_name}
                          </p>
                          <p className="text-sm text-zinc-400">{request.contact_email} {request.contact_phone ? `• ${request.contact_phone}` : ''}</p>
                          {request.message && (
                            <p className="text-sm text-zinc-300 mt-2" data-testid={`pricing-message-${request.request_id}`}>
                              “{request.message}”
                            </p>
                          )}
                          {request.response_message && (
                            <p className="text-xs text-zinc-400 mt-2" data-testid={`pricing-response-${request.request_id}`}>
                              Response: {request.response_message}
                            </p>
                          )}
                          {request.price_amount && (
                            <p className="text-xs text-zinc-300 mt-2" data-testid={`pricing-amount-${request.request_id}`}>
                              Proposed Pricing: {request.price_amount} {request.price_frequency || ''}
                            </p>
                          )}
                          {request.price_notes && (
                            <p className="text-xs text-zinc-500 mt-1" data-testid={`pricing-notes-${request.request_id}`}>
                              Notes: {request.price_notes}
                            </p>
                          )}
                          {(request.contract_start_date || request.contract_end_date) && (
                            <p className="text-xs text-zinc-400 mt-1" data-testid={`pricing-contract-${request.request_id}`}>
                              Contract: {request.contract_start_date || '—'} to {request.contract_end_date || '—'} ({request.contract_term_years || '—'} years)
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-2">
                          <Button
                            size="sm"
                            className="bg-[#FFD60A] text-black hover:bg-[#EAB308]"
                            onClick={() => handleOpenPricingResponse(request)}
                            data-testid={`pricing-respond-button-${request.request_id}`}
                          >
                            Respond
                          </Button>
                          {(() => {
                            const daysRemaining = getContractDaysRemaining(request.contract_end_date);
                            if (daysRemaining === null) {
                              return null;
                            }
                            return (
                              <div className="text-xs text-zinc-400" data-testid={`pricing-contract-remaining-${request.request_id}`}>
                                {daysRemaining >= 0 ? `${daysRemaining} days remaining` : 'Expired'}
                              </div>
                            );
                          })()}
                          {(() => {
                            const daysRemaining = getContractDaysRemaining(request.contract_end_date);
                            if (daysRemaining === null || daysRemaining > 60 || daysRemaining < 0) {
                              return null;
                            }
                            return (
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-[#FFD60A] text-[#FFD60A]"
                                onClick={() => handleSendRenewalReminder(request.request_id)}
                                data-testid={`pricing-renewal-reminder-${request.request_id}`}
                              >
                                Send 60-Day Reminder
                              </Button>
                            );
                          })()}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>

            <Dialog open={!!activePricingRequest} onOpenChange={(open) => !open && setActivePricingRequest(null)}>
              <DialogContent className="sm:max-w-lg" data-testid="pricing-response-dialog">
                <DialogHeader>
                  <DialogTitle>Send Pricing Response</DialogTitle>
                </DialogHeader>
                {activePricingRequest && (
                  <form onSubmit={handleSubmitPricingResponse} className="space-y-4">
                    <div className="rounded-lg bg-zinc-100 p-3 text-xs text-zinc-600" data-testid="pricing-response-summary">
                      {activePricingRequest.property_name} • {activePricingRequest.package_type}
                    </div>
                    <div>
                      <Label htmlFor="pricing-status">Status</Label>
                      <Select
                        value={pricingResponse.status}
                        onValueChange={handlePricingStatusChange}
                      >
                        <SelectTrigger data-testid="pricing-status-select">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="quoted">Quoted</SelectItem>
                          <SelectItem value="needs_info">Needs Info</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="pricing-template-buttons">
                      <Button type="button" variant="outline" size="sm" onClick={() => applyPricingTemplate('quoted')} data-testid="pricing-template-quoted">
                        Use Quoted Template
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => applyPricingTemplate('needs_info')} data-testid="pricing-template-needs-info">
                        Use Needs Info Template
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => applyPricingTemplate('approved')} data-testid="pricing-template-approved">
                        Use Approved Template
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="pricing-amount">Price Amount</Label>
                        <Input
                          id="pricing-amount"
                          value={pricingResponse.price_amount}
                          onChange={(e) => setPricingResponse({ ...pricingResponse, price_amount: e.target.value })}
                          placeholder="$55"
                          data-testid="pricing-amount-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="pricing-frequency">Price Frequency</Label>
                        <Select
                          value={pricingResponse.price_frequency}
                          onValueChange={(value) => setPricingResponse({ ...pricingResponse, price_frequency: value })}
                        >
                          <SelectTrigger data-testid="pricing-frequency-select">
                            <SelectValue placeholder="Select frequency" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="per unit/month">Per Unit / Month</SelectItem>
                            <SelectItem value="per property/month">Per Property / Month</SelectItem>
                            <SelectItem value="per pickup">Per Pickup</SelectItem>
                            <SelectItem value="custom">Custom</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="contract-start">Contract Start Date</Label>
                        <Input
                          id="contract-start"
                          type="date"
                          value={pricingResponse.contract_start_date}
                          onChange={(e) => updateContractTerm({ contract_start_date: e.target.value })}
                          data-testid="pricing-contract-start-date"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contract-term">Contract Term</Label>
                        <Select
                          value={String(pricingResponse.contract_term_years)}
                          onValueChange={(value) => updateContractTerm({ contract_term_years: Number(value) })}
                        >
                          <SelectTrigger data-testid="pricing-contract-term">
                            <SelectValue placeholder="Select term" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 Year</SelectItem>
                            <SelectItem value="2">2 Years</SelectItem>
                            <SelectItem value="3">3 Years</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="contract-end">Contract End Date</Label>
                        <Input
                          id="contract-end"
                          type="date"
                          value={pricingResponse.contract_end_date}
                          readOnly
                          data-testid="pricing-contract-end-date"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="pricing-notes">Pricing Notes</Label>
                      <Textarea
                        id="pricing-notes"
                        value={pricingResponse.price_notes}
                        onChange={(e) => setPricingResponse({ ...pricingResponse, price_notes: e.target.value })}
                        rows={2}
                        placeholder="Optional notes about pricing, discounts, or terms."
                        data-testid="pricing-notes-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pricing-response">Response Message</Label>
                      <Textarea
                        id="pricing-response"
                        value={pricingResponse.response_message}
                        onChange={(e) => setPricingResponse({ ...pricingResponse, response_message: e.target.value })}
                        rows={4}
                        placeholder="Share pricing details or next steps."
                        data-testid="pricing-response-message"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Send Renewal Reminder</Label>
                      <label className="flex items-center gap-2 text-sm text-zinc-600">
                        <input
                          type="checkbox"
                          checked={reminderSettings.send_email}
                          onChange={(e) => setReminderSettings({ ...reminderSettings, send_email: e.target.checked })}
                          data-testid="reminder-send-email"
                        />
                        Email reminder
                      </label>
                      <label className="flex items-center gap-2 text-sm text-zinc-600">
                        <input
                          type="checkbox"
                          checked={reminderSettings.send_sms}
                          onChange={(e) => setReminderSettings({ ...reminderSettings, send_sms: e.target.checked })}
                          data-testid="reminder-send-sms"
                        />
                        SMS reminder
                      </label>
                    </div>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 text-sm text-zinc-600">
                        <input
                          type="checkbox"
                          checked={pricingResponse.send_email}
                          onChange={(e) => setPricingResponse({ ...pricingResponse, send_email: e.target.checked })}
                          data-testid="pricing-send-email"
                        />
                        Send email confirmation
                      </label>
                      <label className="flex items-center gap-2 text-sm text-zinc-600">
                        <input
                          type="checkbox"
                          checked={pricingResponse.send_sms}
                          onChange={(e) => setPricingResponse({ ...pricingResponse, send_sms: e.target.checked })}
                          data-testid="pricing-send-sms"
                        />
                        Send SMS confirmation
                      </label>
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setActivePricingRequest(null)}
                        data-testid="pricing-response-cancel"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        className="font-bold"
                        style={{ backgroundColor: '#C9A962', color: '#2D2D2D' }}
                        data-testid="pricing-response-submit"
                      >
                        Send Response
                      </Button>
                    </div>
                  </form>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Fleet Safety Tab */}
          <TabsContent value="fleet-safety" className="space-y-4">
            <IncidentReportForm regions={regions} vehicles={vehicles} />
            {/* NEW: Region → City → Agent drilldown (per CEO Feb 2026 directive).
                Merges vehicle_safety_reports + safety_incidents by the agent
                who filed them, exposes checklist + police case# + photo gallery,
                and provides a "Route to Safety & Risk Management" action. */}
            <OpsFleetSafetyDrilldown regions={regions} />
            <SafetyIncidentList regions={regions} />
            <Card className="bg-zinc-900 border-zinc-700 p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                      VEHICLE FLEET
                    </h2>
                    <TabHelp
                      title="Vehicle Fleet"
                      testid="fleet-help"
                      items={[
                        { q: 'Who gets a company vehicle?', a: 'Admin staff with responsibilities — CEO, Director, Director Assistant, Manager — plus Site Agents, Supervisors and Crew Leaders.' },
                        { q: 'Why does the form auto-fill?', a: 'When you pick the driver, NECS pulls their department / position / role / state / region from their profile so you don\'t retype anything.' },
                        { q: 'Where does this show up?', a: 'Every assigned vehicle appears as a Fleet Vehicle badge on the employee row in the Employees tab.' },
                      ]}
                    />
                  </div>
                  <p className="text-sm text-zinc-400">Manage company vehicles and their operational status.</p>
                </div>
                {/* Hide the Add Vehicle CTA when the fleet safety tab
                    was reached from the Customer Service HQ — CS reps
                    view fleet + reports and file incidents only; they
                    do not provision vehicles (per CEO Feb 2026). */}
                {parentDept !== 'customer-service' && (
                <Dialog open={showAddVehicle} onOpenChange={(v) => { setShowAddVehicle(v); if (!v) setVehicleStep('choose'); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-teal-600 text-white hover:bg-teal-700" data-testid="fleet-add-vehicle-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Vehicle
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-zinc-900 text-white border-zinc-800 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="fleet-add-vehicle-dialog">
                    <DialogHeader>
                      <DialogTitle data-testid="fleet-add-vehicle-title">
                        {vehicleStep === 'choose' && 'Add Vehicle — choose fleet'}
                        {vehicleStep === 'admin' && 'NECS Admin Vehicle (corporate office)'}
                        {vehicleStep === 'staff' && 'Field Staff Vehicle (site agents)'}
                      </DialogTitle>
                    </DialogHeader>

                    {/* Step 1 — choose which sheet to fill out, exactly
                        like the Add Employee dialog. Keeps admin vehicles
                        and field vehicles cleanly separated so safety
                        majors can be tracked independently for each. */}
                    {vehicleStep === 'choose' && (
                      <div className="grid sm:grid-cols-2 gap-4 mt-2">
                        <Card
                          onClick={() => openVehicleSheet('admin')}
                          className="bg-zinc-800 border-2 border-zinc-700 hover:border-[#C9A962] p-5 cursor-pointer transition"
                          data-testid="fleet-choose-admin"
                        >
                          <ShieldCheck className="h-8 w-8 text-[#C9A962] mb-3" />
                          <h3 className="font-bold text-white text-lg mb-1">NECS Admin Vehicle</h3>
                          <p className="text-xs text-zinc-400 leading-relaxed">
                            Corporate office assignment — CEO, Director, Director Assistant, Manager, Supervisor. Tracks executive fleet safety, mileage, insurance.
                          </p>
                        </Card>
                        <Card
                          onClick={() => openVehicleSheet('staff')}
                          className="bg-zinc-800 border-2 border-zinc-700 hover:border-teal-500 p-5 cursor-pointer transition"
                          data-testid="fleet-choose-staff"
                        >
                          <Truck className="h-8 w-8 text-teal-500 mb-3" />
                          <h3 className="font-bold text-white text-lg mb-1">Field Staff Vehicle</h3>
                          <p className="text-xs text-zinc-400 leading-relaxed">
                            Field Ops crew — Site Agent, Crew Leader, Supervisor, Regional Manager. Tracks pre/post-shift checks, route region, driver license verification.
                          </p>
                        </Card>
                      </div>
                    )}

                    {(vehicleStep === 'admin' || vehicleStep === 'staff') && (
                      <form onSubmit={handleAddVehicle} className="space-y-3">
                        {/* Step 1 — Filter dropdowns (populated). Admin sheet
                            shows Position + Department + State. Staff sheet
                            shows Role + Position + State + Region.  All
                            values feed the Name dropdown filter below. */}
                        <div className="rounded-md border border-zinc-700 p-3 bg-zinc-800/30">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A962] font-bold mb-2">Step 1 · Filter</p>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {vehicleStep === 'staff' && (
                              <div>
                                <Label>Role</Label>
                                <Select value={vehicleFilters.role || '__all__'} onValueChange={(v) => setVehicleFilters({ ...vehicleFilters, role: v === '__all__' ? '' : v })}>
                                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="fleet-filter-role">
                                    <SelectValue placeholder="Any role" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                                    <SelectItem value="__all__">Any role</SelectItem>
                                    <SelectItem value="valet">Valet</SelectItem>
                                    <SelectItem value="cleaner">Cleaner</SelectItem>
                                    <SelectItem value="valet_and_cleaner">Valet + Cleaner</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            <div>
                              <Label>Position</Label>
                              <Select value={vehicleFilters.position || '__all__'} onValueChange={(v) => setVehicleFilters({ ...vehicleFilters, position: v === '__all__' ? '' : v })}>
                                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="fleet-filter-position">
                                  <SelectValue placeholder="Any position" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                                  <SelectItem value="__all__">Any position</SelectItem>
                                  {(vehicleStep === 'admin'
                                    ? ['ceo', 'director', 'director_assistant', 'manager', 'supervisor']
                                    : ['agent', 'crew_leader', 'supervisor']
                                  ).map((p) => (
                                    <SelectItem key={p} value={p}>{p.replace(/_/g, ' ')}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {vehicleStep === 'admin' && (
                              <div>
                                <Label>Department</Label>
                                <Select value={vehicleFilters.department || '__all__'} onValueChange={(v) => setVehicleFilters({ ...vehicleFilters, department: v === '__all__' ? '' : v })}>
                                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="fleet-filter-department">
                                    <SelectValue placeholder="Any department" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-zinc-800 border-zinc-700 text-white max-h-64">
                                    <SelectItem value="__all__">Any department</SelectItem>
                                    {['executive-office', 'hr', 'finance', 'legal', 'sales', 'safety', 'marketing', 'operations', 'customer-service', 'procurement', 'training', 'it'].map((d) => (
                                      <SelectItem key={d} value={d}>{d.replace(/-/g, ' ')}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                            <div>
                              <Label>State</Label>
                              <Select value={vehicleFilters.state || '__all__'} onValueChange={(v) => setVehicleFilters({ ...vehicleFilters, state: v === '__all__' ? '' : v, big_city: '', region: '' })}>
                                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="fleet-filter-state">
                                  <SelectValue placeholder="Any state" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700 text-white max-h-72">
                                  <SelectItem value="__all__">Any state</SelectItem>
                                  {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map((s) => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {/* Big City filter — cascades to the Region dropdown so
                                admins picking a driver for a Dallas run only see
                                Dallas regions. Fixed 5-metro catalog matches the
                                tab strip used everywhere else. */}
                            <div>
                              <Label>Big City</Label>
                              <Select
                                value={vehicleFilters.big_city || '__all__'}
                                onValueChange={(v) => setVehicleFilters({ ...vehicleFilters, big_city: v === '__all__' ? '' : v, region: '' })}
                              >
                                <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="fleet-filter-bigcity">
                                  <SelectValue placeholder="Any big city" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700 text-white max-h-64">
                                  <SelectItem value="__all__">Any big city</SelectItem>
                                  {['Dallas', 'Houston', 'Austin', 'San Antonio', 'Tyler'].map((c) => (
                                    <SelectItem key={c} value={c}>{c}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            {vehicleStep === 'staff' && (
                              <div>
                                <Label>Region</Label>
                                <Select value={vehicleFilters.region || '__all__'} onValueChange={(v) => setVehicleFilters({ ...vehicleFilters, region: v === '__all__' ? '' : v })}>
                                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="fleet-filter-region">
                                    <SelectValue placeholder="Any region" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-zinc-800 border-zinc-700 text-white max-h-72">
                                    <SelectItem value="__all__">Any region</SelectItem>
                                    {(regions || [])
                                      .filter((r) => !vehicleFilters.state || vehicleFilters.state === '__all__' || (r.state || 'TX') === vehicleFilters.state)
                                      .filter((r) => !vehicleFilters.big_city || (r.big_city || 'Unassigned') === vehicleFilters.big_city)
                                      .sort((a, b) => (a.region_code || '').localeCompare(b.region_code || ''))
                                      .map((r) => (
                                        <SelectItem key={r.region_id} value={r.region_id}>
                                          {r.region_code} · {r.name}
                                        </SelectItem>
                                      ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}
                          </div>

                          {/* Step 1b — Full Name dropdown filtered by above. */}
                          <div className="mt-3">
                            <Label>Full Name (from Human Resources)</Label>
                            <Select value={vehicleForm.driver_id || undefined} onValueChange={handleSelectDriver}>
                              <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="fleet-driver-select">
                                <SelectValue placeholder={filteredDrivers.length ? 'Select full name' : 'No matching employees'} />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-700 text-white">
                                {filteredDrivers.length === 0 ? (
                                  <SelectItem value="__no_match__" disabled>
                                    No matches — clear filters or add the employee to HR first.
                                  </SelectItem>
                                ) : (
                                  filteredDrivers.map((d) => (
                                    <SelectItem key={d.user_id} value={d.user_id}>
                                      {d.name}{d.badge_number ? ` · ${d.badge_number}` : ''}
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                            {filteredDrivers.length === 0 && (
                              <p className="mt-2 text-[11px] text-zinc-500 italic">
                                No matches for the current filters — loosen a selection above.
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Step 2 — HR auto-populated read-only profile card.
                            Compact, structured layout: header row with badge +
                            role/position pill, then two-column key/value grid.
                            Row visibility varies per sheet:
                            • Admin: shows Department; hides City.
                            • Staff: shows Role Type; hides Shift. */}
                        {vehicleForm.driver_id && (
                          <div
                            className="rounded-lg border border-[#C9A962]/40 p-3 bg-[#C9A962]/5"
                            data-testid="fleet-hr-snapshot"
                          >
                            <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A962] font-bold mb-2">
                              Step 2 · HR Profile (auto-populated)
                            </p>
                            <div className="flex items-center justify-between gap-3 pb-2 mb-2 border-b border-[#C9A962]/30">
                              <div className="min-w-0">
                                <div className="text-sm font-bold text-white truncate">
                                  {vehicleForm.driver_name || 'Employee'}
                                </div>
                                <div className="text-[11px] text-zinc-400 truncate">
                                  {vehicleForm.position || '—'}{vehicleStep === 'staff' && vehicleForm.role_type ? ` · ${vehicleForm.role_type}` : ''}
                                </div>
                              </div>
                              <span className="inline-flex items-center gap-1 rounded px-2 py-1 bg-zinc-900 border border-[#FFD60A]/40 text-[#FFD60A] font-mono text-[11px] font-bold">
                                {vehicleForm.badge_number || '—'}
                              </span>
                            </div>
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
                              {vehicleStep === 'admin' && (
                                <>
                                  <div className="flex justify-between">
                                    <dt className="text-zinc-500">Department</dt>
                                    <dd className="text-white truncate ml-2">
                                      {vehicleForm.department
                                        ? vehicleForm.department.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                                        : '—'}
                                    </dd>
                                  </div>
                                  <div className="flex justify-between">
                                    <dt className="text-zinc-500">State</dt>
                                    <dd className="text-white font-bold truncate ml-2">{vehicleForm.hr_state || '—'}</dd>
                                  </div>
                                </>
                              )}
                              {vehicleStep === 'staff' && (
                                <>
                                  <div className="flex justify-between">
                                    <dt className="text-zinc-500">Role</dt>
                                    <dd className="text-white truncate ml-2">{vehicleForm.role_type || '—'}</dd>
                                  </div>
                                  <div className="flex justify-between">
                                    <dt className="text-zinc-500">City</dt>
                                    <dd className="text-white truncate ml-2">{vehicleForm.city || '—'}</dd>
                                  </div>
                                  <div className="flex justify-between col-span-2">
                                    <dt className="text-zinc-500">State</dt>
                                    <dd className="text-white font-bold truncate ml-2">{vehicleForm.hr_state || '—'}</dd>
                                  </div>
                                </>
                              )}
                            </dl>
                          </div>
                        )}

                        {/* Step 3 — Vehicle info the admin actually enters. */}
                        <div className="rounded-md border border-teal-500/40 p-3 bg-teal-900/10">
                          <p className="text-[10px] uppercase tracking-[0.2em] text-teal-400 font-bold mb-2">Step 3 · Vehicle Information</p>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <Label>Make</Label>
                              <Input value={vehicleForm.make} onChange={(e) => setVehicleForm({...vehicleForm, make: e.target.value})} placeholder="Ford" className="bg-zinc-800 border-zinc-700" required />
                            </div>
                            <div>
                              <Label>Model</Label>
                              <Input value={vehicleForm.model} onChange={(e) => setVehicleForm({...vehicleForm, model: e.target.value})} placeholder={vehicleStep === 'admin' ? 'Explorer' : 'F-150'} className="bg-zinc-800 border-zinc-700" required />
                            </div>
                            <div>
                              <Label>Mileage</Label>
                              <Input type="number" value={vehicleForm.mileage} onChange={(e) => setVehicleForm({...vehicleForm, mileage: e.target.value})} placeholder="42500" className="bg-zinc-800 border-zinc-700" />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3 mt-3">
                            <div>
                              <Label>License Plate</Label>
                              <Input value={vehicleForm.license_plate} onChange={(e) => setVehicleForm({...vehicleForm, license_plate: e.target.value})} placeholder="ABC-1234" className="bg-zinc-800 border-zinc-700" required data-testid="fleet-vehicle-license-plate" />
                            </div>
                            <div>
                              <Label>Plate State</Label>
                              <Select
                                value={vehicleForm.state || undefined}
                                onValueChange={(v) => setVehicleForm({ ...vehicleForm, state: v })}
                              >
                                <SelectTrigger
                                  className="bg-zinc-800 border-zinc-700"
                                  data-testid="fleet-vehicle-plate-state"
                                >
                                  <SelectValue placeholder="Select state" />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700 text-white max-h-72">
                                  {['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map((s) => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-2">
                          <Button type="button" variant="ghost" className="text-zinc-300 hover:bg-zinc-800" onClick={() => setVehicleStep('choose')} data-testid="fleet-add-vehicle-back">
                            ← Choose another sheet
                          </Button>
                          <Button
                            type="submit"
                            disabled={!vehicleForm.driver_id}
                            className={vehicleStep === 'admin' ? 'bg-[#C9A962] text-black hover:opacity-90 font-bold disabled:opacity-40' : 'bg-teal-600 text-white hover:bg-teal-700 font-bold disabled:opacity-40'}
                            data-testid="fleet-add-vehicle-submit"
                          >
                            {vehicleStep === 'admin' ? 'Add Admin Vehicle' : 'Add Field Vehicle'}
                          </Button>
                        </div>
                      </form>
                    )}
                  </DialogContent>
                </Dialog>
                )}
              </div>

              {/* Vehicles split into two sheets — admin office + field
                  ops — so safety majors can be tracked independently for
                  each party (matches the Add Vehicle dialog flow). */}
              {['admin', 'staff'].map((kind) => {
                const list = vehicles.filter((v) => (v.fleet_kind || 'staff') === kind);
                const accent = kind === 'admin' ? '#C9A962' : '#14b8a6';
                const label = kind === 'admin' ? 'NECS ADMIN FLEET' : 'FIELD STAFF FLEET';
                const sub = kind === 'admin' ? 'Corporate office vehicles' : 'Site agents · valet · cleaner crews';
                return (
                  <div key={kind} className="mb-6" data-testid={`fleet-sheet-${kind}`}>
                    <div className="flex items-center justify-between mb-3 pb-2" style={{ borderBottom: `2px solid ${accent}` }}>
                      <div>
                        <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.05em' }}>
                          {label} <span style={{ color: accent }}>· {list.length}</span>
                        </h3>
                        <p className="text-xs text-zinc-400">{sub}</p>
                      </div>
                    </div>
                    {list.length === 0 ? (
                      <p className="text-zinc-500 text-xs italic mb-4">No {kind === 'admin' ? 'admin' : 'field'} vehicles added yet.</p>
                    ) : (
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {list.map((v) => (
                          <Card key={v.vehicle_id} className="bg-zinc-800 border-zinc-700 p-4" data-testid={`fleet-vehicle-${v.vehicle_id}`} style={{ borderLeft: `4px solid ${accent}` }}>
                            <div className="flex justify-between items-start mb-2">
                              <div className="min-w-0">
                                <p className="font-bold text-white text-lg truncate">{v.make_model || `${v.make || ''} ${v.model || ''}`}</p>
                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                  <Badge className="bg-zinc-700">{v.license_plate}</Badge>
                                  {(() => {
                                    // State Coverage badge: green when the plate
                                    // state matches the driver's HR state, amber
                                    // when out-of-state, red when missing.
                                    const plate = (v.state || '').toUpperCase();
                                    const hr = (v.hr_state || '').toUpperCase();
                                    if (!plate) {
                                      return (
                                        <Badge
                                          className="bg-red-700 text-white text-[10px] font-bold"
                                          data-testid={`fleet-vehicle-coverage-${v.vehicle_id}`}
                                          title="Plate state missing"
                                        >
                                          NO STATE
                                        </Badge>
                                      );
                                    }
                                    const inState = hr && plate === hr;
                                    return (
                                      <Badge
                                        className={`text-[10px] font-bold ${
                                          inState
                                            ? 'bg-emerald-700 text-white'
                                            : 'bg-amber-700 text-white'
                                        }`}
                                        data-testid={`fleet-vehicle-coverage-${v.vehicle_id}`}
                                        title={inState
                                          ? `Plate state matches driver's HR state (${hr})`
                                          : `Plate ${plate} · HR state ${hr || 'unknown'}`}
                                      >
                                        {plate}{!inState && hr ? ` ≠ ${hr}` : ''}
                                      </Badge>
                                    );
                                  })()}
                                </div>
                              </div>
                              <Select value={v.status} onValueChange={(val) => handleUpdateVehicleStatus(v.vehicle_id, val)}>
                                <SelectTrigger className={`w-[140px] ${v.status === 'operational' ? 'bg-green-900/30 text-green-500 border-green-500' : v.status === 'maintenance' ? 'bg-amber-900/30 text-amber-500 border-amber-500' : 'bg-red-900/30 text-red-500 border-red-500'}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700">
                                  <SelectItem value="operational">Operational</SelectItem>
                                  <SelectItem value="maintenance">Maintenance</SelectItem>
                                  <SelectItem value="non_operational">Non-Operational</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {/* Inspection countdown — green when fresh,
                                amber when ≤14 days out, red when overdue
                                or never inspected. Driven by the last
                                pre-shift safety report. */}
                            {(() => {
                              const dti = v.days_to_inspection;
                              let label, cls;
                              if (dti == null) { label = 'Never Inspected'; cls = 'bg-red-700 text-white'; }
                              else if (dti < 0) { label = `Overdue ${Math.abs(dti)}d`; cls = 'bg-red-700 text-white'; }
                              else if (dti <= 14) { label = `Inspection in ${dti}d`; cls = 'bg-amber-600 text-white'; }
                              else { label = `Inspection in ${dti}d`; cls = 'bg-green-700 text-white'; }
                              return (
                                <Badge className={`${cls} text-[10px] font-bold mt-1`} data-testid={`fleet-vehicle-inspection-${v.vehicle_id}`}>
                                  {label}
                                </Badge>
                              );
                            })()}
                            <div className="mt-3 pt-3 border-t border-zinc-700 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-zinc-400">
                              {v.driver_name && <div className="col-span-2"><span className="text-zinc-500">Driver:</span> <span className="text-white font-bold">{v.driver_name}</span>{v.badge_number ? <span className="text-[#FFD60A] font-mono ml-2">{v.badge_number}</span> : null}</div>}
                              {v.department && <div><span className="text-zinc-500">Dept:</span> {v.department}</div>}
                              {v.position && <div><span className="text-zinc-500">Position:</span> {v.position}</div>}
                              {v.role && <div><span className="text-zinc-500">Role:</span> {v.role}</div>}
                              {v.role_type && <div><span className="text-zinc-500">Type:</span> {v.role_type}</div>}
                              {v.mileage != null && <div><span className="text-zinc-500">Miles:</span> {Number(v.mileage).toLocaleString()}</div>}
                              {v.assigned_shift && <div className="col-span-2"><span className="text-zinc-500">Shift:</span> {v.assigned_shift}</div>}
                              {v.city && <div><span className="text-zinc-500">City:</span> {v.city}</div>}
                              {v.state && <div><span className="text-zinc-500">State:</span> {v.state}</div>}
                              {v.region && <div><span className="text-zinc-500">Region:</span> {v.region}</div>}
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>

            <Card className="bg-zinc-900 border-2 border-teal-500/40 p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                    FLEET SAFETY REPORTS

                  </h2>
                  <p className="text-sm text-zinc-400">Review daily vehicle inspections from supervisors and crew leaders.</p>
                </div>
                <Badge className="bg-teal-600 text-white">
                  {safetyReports.length} Reports
                </Badge>
              </div>

              {safetyReports.length === 0 ? (
                <div className="text-center py-10">
                  <Truck className="h-12 w-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400">No safety reports submitted yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {safetyReports.map((report) => (
                    <Card key={report.report_id} className="bg-zinc-800 border-zinc-700 p-4">
                      <div className="flex flex-col md:flex-row gap-6">
                        <div className="flex-1">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="text-white font-bold">{report.user_name}</p>
                              <p className="text-xs text-zinc-400">Badge: {report.badge_number} • {new Date(report.created_at).toLocaleString()}</p>
                            </div>
                            <div className="text-right">
                              <Badge className={report.check_type === 'pre_shift' ? 'bg-blue-600 mb-2' : 'bg-purple-600 mb-2'}>
                                {report.check_type === 'pre_shift' ? 'Pre-Shift Check' : 'Post-Shift Check'}
                              </Badge>
                              {report.check_type === 'pre_shift' && (
                                <Badge className={`block ${report.has_license ? 'bg-green-600' : 'bg-red-600'}`}>
                                  {report.has_license ? 'License Verified' : 'No License'}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="mb-4">
                            <p className="text-sm text-zinc-400">Vehicle:</p>
                            <Badge className="bg-zinc-700 text-white mt-1 text-sm">{report.vehicle_name || 'Unknown Vehicle'}</Badge>
                          </div>
                          
                          <div className="mt-4">
                            <p className="text-sm text-zinc-400 mb-2">Checklist Summary:</p>
                            <div className="flex flex-wrap gap-2">
                              <Badge className="bg-blue-900 text-blue-300 border-blue-700">Fuel: {report.fuel_level}</Badge>
                              {Object.entries(report.checklist || {}).map(([key, value]) => (
                                <Badge key={key} variant="outline" className={`border ${value ? 'border-green-500 text-green-400' : 'border-zinc-600 text-zinc-500'}`}>
                                  {key.replace(/_/g, ' ')}
                                </Badge>
                              ))}
                            </div>
                          </div>

                          {report.notes && (
                            <div className="mt-4 p-3 bg-zinc-900 rounded border border-zinc-700">
                              <p className="text-xs text-zinc-400 mb-1">Notes/Incidents:</p>
                              <p className="text-sm text-white">{report.notes}</p>
                            </div>
                          )}
                        </div>

                        <div className="w-full md:w-48 space-y-4">
                          {report.license_photo && (
                            <div>
                              <p className="text-xs text-zinc-400 mb-1">Driver's License</p>
                              <img 
                                src={report.license_photo} 
                                alt="License" 
                                className="w-full h-24 object-cover rounded border border-zinc-600 cursor-pointer hover:opacity-80"
                                onClick={() => window.open(report.license_photo, '_blank')}
                              />
                            </div>
                          )}
                          {report.gas_receipt_photo && (
                            <div>
                              <p className="text-xs text-zinc-400 mb-1">Gas Receipt</p>
                              <img 
                                src={report.gas_receipt_photo} 
                                alt="Gas Receipt" 
                                className="w-full h-24 object-cover rounded border border-zinc-600 cursor-pointer hover:opacity-80"
                                onClick={() => window.open(report.gas_receipt_photo, '_blank')}
                              />
                            </div>
                          )}
                          {report.vehicle_photos && report.vehicle_photos.length > 0 && (
                            <div>
                              <p className="text-xs text-zinc-400 mb-1">Vehicle Photos ({report.vehicle_photos.length})</p>
                              <div className="grid grid-cols-2 gap-2">
                                {report.vehicle_photos.slice(0, 4).map((photo, idx) => (
                                  <img 
                                    key={idx}
                                    src={photo.data} 
                                    alt={`Vehicle ${idx+1}`} 
                                    className="w-full h-12 object-cover rounded border border-zinc-600 cursor-pointer hover:opacity-80"
                                    onClick={() => window.open(photo.data, '_blank')}
                                  />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>

          {/* Daily Reports Tab */}
          <TabsContent value="daily-reports" className="space-y-4">
            <ServiceSignalsLegend
              propertyIds={properties.map((p) => p.property_id)}
              className="mb-2"
            />
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={dailyReports}
              getItemPropertyId={(r) => r.property_id}
              sectionLabel="Daily Reports"
              sectionIcon={ClipboardList}
              renderItems={(items) => (
                <ul className="space-y-1 text-[11px] text-zinc-200">
                  {items.map((r) => (
                    <li key={r.report_id || r._id} className="border border-zinc-800 rounded px-2 py-1">
                      <div className="flex justify-between gap-2">
                        <span className="font-bold text-white truncate">{r.date || 'Report'}</span>
                        <span className="text-zinc-500 flex-shrink-0 text-[10px]">{r.units_serviced ?? '—'} units</span>
                      </div>
                      <div className="text-zinc-400 truncate">By {r.staff_name || r.user_id || '—'}</div>
                    </li>
                  ))}
                </ul>
              )}
              emptyItemsLabel="No daily reports for this property."
              testIdPrefix="drilldown-daily"
            />
            <AdminDailyReportsTab properties={properties} dailyReports={dailyReports} />
          </TabsContent>

          {/* Contracts Tab — extracted to `dashboard/tabs/ContractsTab.jsx` */}
          <TabsContent value="contracts" className="space-y-4">
            <MetroKindHeader
              bigCity={companyMetro}
              onBigCity={setCompanyMetro}
              testid="contracts"
            />
            <ContractsTab
              contractForm={contractForm}
              setContractForm={setContractForm}
              handleContractUpload={handleContractUpload}
              uploadingContract={uploadingContract}
              properties={properties}
              contracts={contracts}
              bigCity={companyMetro}
            />
          </TabsContent>

          {/* QR Codes Tab */}
          <TabsContent value="qr-codes" className="space-y-4">
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={[]}
              sectionLabel="QR Codes"
              sectionIcon={QrCode}
              emptyItemsLabel={`QR codes are per ${scopeTerms.occupant.toLowerCase()} unit — select a property.`}
              testIdPrefix="drilldown-qr"
            />
            <QRCodesTab dataTestId="company-qr-codes" />
          </TabsContent>

          {/* Updates & Notices (primary) + Training sub-tab (preserved) */}
          <TabsContent value="training" className="space-y-4">
            <Tabs defaultValue="updates-feed" className="space-y-4">
              <TabsList>
                <TabsTrigger value="updates-feed" data-testid="admin-updates-subtab">Updates &amp; Notices</TabsTrigger>
                <TabsTrigger value="training-hub" data-testid="admin-training-subtab">Training</TabsTrigger>
              </TabsList>
              <TabsContent value="updates-feed" className="space-y-4">
                <AdminUpdatesManager />
              </TabsContent>
              <TabsContent value="training-hub" className="space-y-4">
                <MetroKindHeader
                  bigCity={companyMetro}
                  onBigCity={setCompanyMetro}
                  kind={companyMetroKind}
                  onKind={setCompanyMetroKind}
                  testid="training"
                />
                <TrainingHub dataTestId="company-training-hub" bigCity={companyMetro} kind={companyMetroKind} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Billing Tab */}
          <TabsContent value="billing" className="space-y-4">
            <MetroKindHeader
              bigCity={companyMetro}
              onBigCity={setCompanyMetro}
              kind={companyMetroKind}
              onKind={setCompanyMetroKind}
              testid="billing"
            />
            <BillingHub dataTestId="company-billing-hub" bigCity={companyMetro} kind={companyMetroKind} />
          </TabsContent>

          {/* Weekly Report Tab */}
          <TabsContent value="weekly" className="space-y-4">
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={[]}
              sectionLabel="Weekly Report"
              sectionIcon={Trophy}
              emptyItemsLabel="Weekly Best Agent / attendance bonus are agent-scoped — full weekly cards below."
              testIdPrefix="drilldown-weekly"
            />
            {weeklyReport && (
              <>
                {/* Best Agent Card */}
                {weeklyReport.best_agent && (
                  <Card className="bg-gradient-to-r from-amber-900/50 to-yellow-900/50 border-2 border-[#FFD60A] p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="relative">
                          <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center overflow-hidden border-4 border-[#FFD60A]">
                            {weeklyReport.best_agent.profile_photo ? (
                              <img src={weeklyReport.best_agent.profile_photo} alt="Best Agent" className="w-full h-full object-cover" />
                            ) : (
                              <User className="h-10 w-10 text-[#FFD60A]" />
                            )}
                          </div>
                          <Trophy className="absolute -top-2 -right-2 h-8 w-8 text-[#FFD60A]" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-[#FFD60A]" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                            BEST AGENT OF THE WEEK
                          </h2>
                          <p className="text-xl text-white font-bold">{weeklyReport.best_agent.user_name}</p>
                          <Badge className="bg-[#FFD60A] text-black">{weeklyReport.best_agent.badge_number}</Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-4xl font-black text-[#FFD60A]">{weeklyReport.best_agent.pickup_count}</p>
                        <p className="text-sm text-zinc-400">Total Pickups</p>
                      </div>
                    </div>
                  </Card>
                )}

                {/* Week Info */}
                <Card className="bg-zinc-900 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-lg text-white">
                      Week: {weeklyReport.week_start} to {weeklyReport.week_end}
                    </h3>
                    <Button 
                      onClick={handleArchiveWeeklyReport}
                      className="bg-[#FFD60A] text-black hover:bg-[#EAB308]"
                    >
                      <Award className="h-4 w-4 mr-2" />
                      Archive for Bonuses
                    </Button>
                  </div>

                  {/* Leaderboard */}
                  <h4 className="font-bold text-white mb-3 flex items-center">
                    <Star className="h-4 w-4 text-[#FFD60A] mr-2" />
                    TOP 10 PERFORMERS
                  </h4>
                  <div className="space-y-2">
                    {weeklyReport.leaderboard?.map((agent, idx) => (
                      <div key={agent.user_id} className={`flex items-center p-3 rounded ${
                        idx === 0 ? 'bg-amber-900/30 border border-[#FFD60A]' :
                        idx === 1 ? 'bg-zinc-700' :
                        idx === 2 ? 'bg-amber-800/20' : 'bg-zinc-800'
                      }`}>
                        <span className={`text-lg font-bold w-8 ${idx < 3 ? 'text-[#FFD60A]' : 'text-zinc-400'}`}>
                          #{idx + 1}
                        </span>
                        <div className="w-10 h-10 rounded-full bg-zinc-700 mr-3 flex items-center justify-center overflow-hidden">
                          {agent.profile_photo ? (
                            <img src={agent.profile_photo} alt={agent.user_name} className="w-full h-full object-cover" />
                          ) : (
                            <User className="h-5 w-5 text-zinc-400" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="text-white font-medium">{agent.user_name}</p>
                          <p className="text-xs text-zinc-400">{agent.badge_number}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-[#FFD60A]">{agent.pickup_count}</p>
                          <p className="text-xs text-zinc-400">pickups</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Bonus Eligible */}
                <Card className="bg-zinc-900 border-2 border-green-500/50 p-6">
                  <h3 className="font-bold text-lg mb-4 flex items-center text-white">
                    <Award className="h-5 w-5 text-green-500 mr-2" />
                    FULL ATTENDANCE BONUS ELIGIBLE
                  </h3>
                  <p className="text-sm text-zinc-400 mb-4">
                    Employees who completed all scheduled shifts this week
                  </p>
                  {weeklyReport.full_attendance_bonus_eligible?.length === 0 ? (
                    <p className="text-zinc-500 text-center py-4">No employees with perfect attendance this week</p>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-3">
                      {weeklyReport.full_attendance_bonus_eligible?.map((agent) => (
                        <Card key={agent.user_id} className="bg-green-900/20 border-green-600 p-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full bg-zinc-700 flex items-center justify-center overflow-hidden">
                              {agent.profile_photo ? (
                                <img src={agent.profile_photo} alt={agent.user_name} className="w-full h-full object-cover" />
                              ) : (
                                <User className="h-5 w-5 text-zinc-400" />
                              )}
                            </div>
                            <div>
                              <p className="text-white font-medium">{agent.user_name}</p>
                              <p className="text-xs text-zinc-400">{agent.badge_number} • {agent.scheduled_days} days • {agent.pickup_count} pickups</p>
                            </div>
                            <CheckCircle className="h-5 w-5 text-green-500 ml-auto" />
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </Card>
              </>
            )}
          </TabsContent>

          {/* Uber Trash transparency dashboard */}
          <TabsContent value="uber-trash" className="space-y-4">
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={[]}
              sectionLabel="Uber Trash"
              sectionIcon={Truck}
              emptyItemsLabel={`Uber Trash roster · ${scopeTerms.occupants}`}
              testIdPrefix="drilldown-uber-trash"
            />
            <UberTrashDashboard boardFilter={deptBoardFilter} />
          </TabsContent>

          {/* Non-operating dates — mirrors the Violations tab structure:
              scope filter (Global · State · Big-city · Region), grouped list,
              and a one-tap federal-holiday seeder for the 7 fixed dates. */}
          <TabsContent value="blackouts" className="space-y-4" data-testid="admin-blackouts-content">
            <MetroKindHeader
              bigCity={companyMetro}
              onBigCity={setCompanyMetro}
              testid="blackouts"
            />
            <AdminBlackoutDates regions={regions} />
          </TabsContent>

          {/* Demo Tools */}
          <TabsContent value="demo-tools" className="space-y-4">
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={[]}
              sectionLabel="Demo Tools"
              sectionIcon={Sparkles}
              emptyItemsLabel="Demo cases follow the Corporate / Valet / Cleaners selection."
              testIdPrefix="drilldown-demo"
            />
            <ScopedDemoSuite viewAs={deptBoardFilter.view_as} />
          </TabsContent>

          {/* Bulk Removal (formerly "Bulk Pickup") — the tab trigger has
              been retired from the top nav; users reach this section via
              the PROPERTY SPECIALTY SERVICES tile grid on the Ops home.
              The route content itself is preserved below. */}
          <TabsContent value="bulk-pickup" className="space-y-4">
            <AdminBulkPickup />
          </TabsContent>

          {/* Property Specialty Services — per-service scope, prep, crew guidance & PM request queue */}
          <TabsContent value="specialty-services" className="space-y-4">
            <AdminSpecialtyServices initialType={specialtyServiceType} />
          </TabsContent>

          {/* Routes & Regions */}
          <TabsContent value="routes-regions" className="space-y-4">
            <AdminRoutesRegions />
          </TabsContent>

          {/* Live Tracking */}
          <TabsContent value="live-tracking" className="space-y-4">
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={[]}
              sectionLabel="Live Tracking"
              sectionIcon={MapPin}
              emptyItemsLabel="Live GPS positions are agent-scoped — full map below."
              testIdPrefix="drilldown-tracking"
            />
            <LiveTrackingMap />
          </TabsContent>

          {/* Iter-90 · Live Ops — Operations Intelligence surface.
              Composes the Valet Leaderboard, live routes map, and Gemini
              accuracy trend inside the Operations department (architecture
              rule: department views render before the CEO tile is wired). */}
          <TabsContent value="live-ops" className="space-y-4" data-testid="admin-live-ops-content">
            <RouteAlertsPanel />
            <LeaderboardCard department="valet" />
            <LiveRoutesMap />
            <AiAccuracyTrendCard />
            <AlertConfigCard canEdit />
          </TabsContent>

          {/* Live Support Chats */}
          <TabsContent value="support-chat" className="space-y-4">
            <OpsRegionalDrilldown
              regions={regions}
              properties={properties}
              items={[]}
              sectionLabel="Live Chats"
              sectionIcon={FileText}
              emptyItemsLabel="Support chats aren't property-scoped — full inbox below."
              testIdPrefix="drilldown-chats"
            />
            <AdminSupportChat />
          </TabsContent>

          {/* Access Requests — list/approve pending NECS Staff/Admin signups */}
          <TabsContent value="access-requests" className="space-y-4">
            <AccessRequestsTab />
          </TabsContent>

          {/* Attendance — every NECS employee scorecard + admin event log */}
          <TabsContent value="attendance" className="space-y-4">
            <AttendanceTab user={user} />
          </TabsContent>

          {/* Brokers / Providers — supplier ledger + per-broker purchase entries */}
          <TabsContent value="brokers" className="space-y-4">
            <BrokersTab />
          </TabsContent>

          {/* Sales Leads — inbound demo signups from the public landing page */}
          <TabsContent value="sales-leads" className="space-y-4">
            <SalesLeadsTab />
          </TabsContent>

          {/* Master Addendum · R&R sub-tabs + Leads CRM removed from
              Operations. Leads CRM lives exclusively in Customer Services
              (see DeptCommandCenters.jsx). */}
        </Tabs>
      </div>
    )}
    </div>
    </DashboardShell>
    </AdminBoardFilterContext.Provider>
    </DepartmentNavigationProvider>
    </>
  );
}


// Iter-105 · Item 1 — Per-department filter mount. Rendered above each
// department board so operator changes on one dept never touch another.
// Iter-111 · DepartmentBoardFilter wrapper retired — the filter instance
// is lifted to the parent (deptBoardFilter) and rendered inside each
// command center below its header + key responsibilities.
